import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { useTemplateStore, useRenderStore, useAuthStore } from "@/store";
import { useLocalizedNavigation } from "@/hooks/useLocalizedNavigation";
import LocalizedLink from "@/components/LocalizedLink";
import { translateCurrentPathText as t } from "@/lib/translations";
import { stripLanguagePrefix } from "@/lib/i18n";
import { getTextPositionPreset, TEXT_POSITION_PRESETS } from "@/lib/textPositionPresets";
import { getLayerPositionX } from "@/lib/layerPosition";
import { toast } from "sonner";
import {
  Play,
  Pause,
  ArrowLeft,
  Download,
  Upload,
  Image,
  Type,
  Video,
  Camera,
  Sparkles,
  Loader2,
  ChevronRight,
  Settings,
  Eye,
  Maximize2,
  X,
  Bold,
  Italic,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Zap,
  Trash2,
  Music,
  Square,
  Plus,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import axios from "axios";
import ShapeLayer from "@/components/ShapeLayer";
import ShapeSettings from "@/components/ShapeSettings";
import TextWipeLayer from "@/components/TextWipeLayer";
import { buildCustomTimeline, createGroup, CUSTOM_TIMELINE_KEY, getInitialCustomTimeline, removeCustomScene } from "@/lib/customTimeline";
import { getAnimationAudioGain } from "@/lib/audioTransition";

const getLocalMediaDuration = (file) => new Promise((resolve) => {
  if (!file || (!file.type.startsWith("video/") && !file.type.startsWith("audio/"))) {
    resolve(null);
    return;
  }

  const element = document.createElement(file.type.startsWith("audio/") ? "audio" : "video");
  const objectUrl = URL.createObjectURL(file);
  const cleanup = () => URL.revokeObjectURL(objectUrl);

  element.preload = "metadata";
  element.onloadedmetadata = () => {
    const duration = Number.isFinite(element.duration) && element.duration > 0 ? element.duration : null;
    cleanup();
    resolve(duration);
  };
  element.onerror = () => {
    cleanup();
    resolve(null);
  };
  element.src = objectUrl;
});

const getMediaCornerRadiusStyle = (layer, custom = {}, scale = 1) => {
  const all = custom.borderRadius ?? layer.borderRadius ?? 0;
  const topLeft = custom.borderTopLeftRadius ?? layer.borderTopLeftRadius ?? all;
  const topRight = custom.borderTopRightRadius ?? layer.borderTopRightRadius ?? all;
  const bottomRight = custom.borderBottomRightRadius ?? layer.borderBottomRightRadius ?? all;
  const bottomLeft = custom.borderBottomLeftRadius ?? layer.borderBottomLeftRadius ?? all;

  return {
    overflow: "hidden",
    borderTopLeftRadius: `${Number(topLeft || 0) * scale}px`,
    borderTopRightRadius: `${Number(topRight || 0) * scale}px`,
    borderBottomRightRadius: `${Number(bottomRight || 0) * scale}px`,
    borderBottomLeftRadius: `${Number(bottomLeft || 0) * scale}px`,
  };
};

// Auto font-sizing utility function
const getBackendTextMetrics = (containerWidth, containerHeight, style = {}) => {
  const textWidth = Math.max(1, Math.round(Number(containerWidth) || 0));
  const textHeight = Math.max(1, Math.round(Number(containerHeight) || 0));
  const defaultPad = Math.max(12, Math.floor(Math.min(textWidth, textHeight) * 0.06));
  const rawPaddingH = style.padding ?? style.paddingX ?? style.paddingHorizontal ?? defaultPad;
  const rawPaddingV = style.paddingY ?? style.paddingVertical ?? rawPaddingH;

  const parsePadding = (value, fallback) => {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? Math.trunc(parsed) : fallback;
  };

  const paddingH = Math.max(0, Math.min(parsePadding(rawPaddingH, defaultPad), Math.floor(textWidth / 3)));
  const paddingV = Math.max(0, Math.min(parsePadding(rawPaddingV, paddingH), Math.floor(textHeight / 3)));
  const innerW = Math.max(10, textWidth - paddingH * 2);
  const innerH = Math.max(10, textHeight - paddingV * 2);
  const descenderGuard = Math.max(6, Math.floor(innerH * 0.10));
  const safeInnerW = innerW;
  const safeInnerH = Math.max(10, innerH - descenderGuard);

  return {
    paddingH,
    paddingV,
    innerW,
    innerH,
    safeInnerW,
    safeInnerH,
    textX: paddingH,
    textY: paddingV + Math.round((innerH - safeInnerH) / 2),
  };
};

// Auto font-sizing utility function
const calculateAutoFontSize = (containerWidth, containerHeight, text, style = {}) => {
  if (!text || !containerWidth || !containerHeight) return 16;

  const metrics = getBackendTextMetrics(containerWidth, containerHeight, style);
  const fitWidth = metrics.safeInnerW;
  const fitHeight = metrics.safeInnerH;
  const temp = document.createElement('div');
  temp.style.position = 'absolute';
  temp.style.visibility = 'hidden';
  temp.style.width = `${fitWidth}px`;
  temp.style.whiteSpace = 'pre-wrap';
  temp.style.overflowWrap = 'normal';
  temp.style.wordBreak = 'normal';
  temp.style.lineHeight = style.lineHeight || '1.5';
  temp.style.fontFamily = style.fontFamily || 'Arial';
  temp.style.fontWeight = style.fontWeight || 'normal';
  temp.style.fontStyle = style.fontStyle || 'normal';
  temp.style.boxSizing = 'border-box';
  temp.textContent = text;
  document.body.appendChild(temp);

  const measureCanvas = calculateAutoFontSize._measureCanvas || document.createElement('canvas');
  calculateAutoFontSize._measureCanvas = measureCanvas;
  const measureContext = measureCanvas.getContext('2d');
  const words = String(text).split(/\s+/).filter(Boolean);

  const longestWordFits = (fontSize) => {
    if (!measureContext || words.length === 0) return true;
    measureContext.font = `${style.fontStyle || 'normal'} ${style.fontWeight || 'normal'} ${fontSize}px ${style.fontFamily || 'Arial'}`;
    return words.every((word) => measureContext.measureText(word).width <= fitWidth);
  };

  let minSize = 8;
  let maxSize = 200;
  let optimalSize = minSize;

  while (minSize <= maxSize) {
    const midSize = Math.floor((minSize + maxSize) / 2);
    temp.style.fontSize = `${midSize}px`;

    if (longestWordFits(midSize) && temp.offsetHeight <= fitHeight) {
      optimalSize = midSize;
      minSize = midSize + 1;
    } else {
      maxSize = midSize - 1;
    }
  }

  document.body.removeChild(temp);
  return optimalSize;
};

const hexToRgba = (hex, alpha = 1) => {
  const normalized = (hex || "#000000").replace("#", "");
  const safeHex = normalized.length === 3
    ? normalized.split("").map((char) => char + char).join("")
    : normalized.padEnd(6, "0").slice(0, 6);

  const r = parseInt(safeHex.slice(0, 2), 16) || 0;
  const g = parseInt(safeHex.slice(2, 4), 16) || 0;
  const b = parseInt(safeHex.slice(4, 6), 16) || 0;

  return `rgba(${r}, ${g}, ${b}, ${Math.max(0, Math.min(1, alpha))})`;
};


const isPixverseReversePreviewLayer = (layer, mediaUrl) => {
  return Boolean(
    layer?.type === "image" &&
    layer?.pixverse_reverse_clip !== false &&
    (layer?.pixverse_generated === true || String(mediaUrl || "").toLowerCase().includes("pixverse"))
  );
};

const getPreviewVideoTargetTime = ({
  videoStartTime,
  layerDuration,
  sourceDuration,
  trimStart = 0,
  trimEnd = 0,
  uploadedVideoStart = 0,
  freezeFirst = false,
  freezeLast = false,
  reverse = false,
}) => {
  const safeSourceDuration = Math.max(0, sourceDuration || layerDuration || 0);
  const safeUploadedVideoStart = Math.max(0, Math.min(uploadedVideoStart, safeSourceDuration));
  const safeTrimStart = Math.max(0, Math.min(trimStart + safeUploadedVideoStart, safeSourceDuration));
  const safeTrimEnd = Math.max(0, Math.min(trimEnd, Math.max(0, safeSourceDuration - safeTrimStart)));
  const segmentStart = safeTrimStart;
  const segmentEnd = Math.max(segmentStart, safeSourceDuration - safeTrimEnd);
  const availableDuration = Math.max(0.001, segmentEnd - segmentStart);
  const freezeDuration = Math.max(0, (layerDuration || 0) - availableDuration);
  let firstFreezeDuration = freezeFirst ? freezeDuration : 0;

  if (freezeFirst && freezeLast) {
    firstFreezeDuration = freezeDuration / 2;
  }

  const playbackTime = Math.max(0, Math.min(videoStartTime - firstFreezeDuration, availableDuration));
  const targetTime = reverse
    ? Math.max(segmentStart, segmentEnd - playbackTime - 0.04)
    : segmentStart + playbackTime;

  return Math.max(0, Math.min(targetTime, Math.max(0, safeSourceDuration - 0.04)));
};

const buildTextEffectShadow = ({
  strokeWidth = 0,
  strokeColor = "#000000",
  scaleDivisor = 3,
}) => {
  const effects = [];
  const outsideStroke = (strokeWidth || 0) / scaleDivisor;

  if (outsideStroke > 0) {
    const c = hexToRgba(strokeColor, 1);
    effects.push(
      `${outsideStroke}px 0 0 ${c}`,
      `${-outsideStroke}px 0 0 ${c}`,
      `0 ${outsideStroke}px 0 ${c}`,
      `0 ${-outsideStroke}px 0 ${c}`,
      `${outsideStroke}px ${outsideStroke}px 0 ${c}`,
      `${outsideStroke}px ${-outsideStroke}px 0 ${c}`,
      `${-outsideStroke}px ${outsideStroke}px 0 ${c}`,
      `${-outsideStroke}px ${-outsideStroke}px 0 ${c}`
    );
  }

  return effects.length > 0 ? effects.join(", ") : "none";
};

// Animation preview helper functions
const cubicBezier = (t, p1, p2) => {
  const oneMinusT = 1 - t;
  return (3 * oneMinusT * oneMinusT * t * p1) + (3 * oneMinusT * t * t * p2) + (t * t * t);
};

const cubicBezierDerivative = (t, p1, p2) => {
  const oneMinusT = 1 - t;
  return (3 * oneMinusT * oneMinusT * p1) + (6 * oneMinusT * t * (p2 - p1)) + (3 * t * t * (1 - p2));
};

const solveBezierProgress = (progress, x1, y1, x2, y2) => {
  let t = progress;

  for (let i = 0; i < 5; i += 1) {
    const currentX = cubicBezier(t, x1, x2) - progress;
    const currentSlope = cubicBezierDerivative(t, x1, x2);

    if (Math.abs(currentSlope) < 1e-6) break;
    t -= currentX / currentSlope;
    t = Math.min(Math.max(t, 0), 1);
  }

  let lower = 0;
  let upper = 1;
  for (let i = 0; i < 8; i += 1) {
    const currentX = cubicBezier(t, x1, x2);
    if (Math.abs(currentX - progress) < 1e-5) break;
    if (currentX > progress) {
      upper = t;
    } else {
      lower = t;
    }
    t = (lower + upper) / 2;
  }

  return cubicBezier(t, y1, y2);
};

const applyAnimationEasing = (progress, easing = "linear") => {
  const clampedProgress = Math.min(Math.max(progress, 0), 1);

  switch (easing) {
    case "ease_in":
      return solveBezierProgress(clampedProgress, 0.55, 0, 1, 1);
    case "ease_out":
      return solveBezierProgress(clampedProgress, 0, 0, 0.45, 1);
    default:
      return clampedProgress;
  }
};

const getEasedExitProgress = (progress, easing = "linear") => {
  const exitPhase = 1 - Math.min(Math.max(progress, 0), 1);
  return 1 - applyAnimationEasing(exitPhase, easing);
};

const getLayerAnimationStyle = (layer, customizations, currentTime, isPlaying, canvasWidth, canvasHeight) => {
  if (!isPlaying) return {};

  const startTime = layer.start_time || 0;
  const duration = layer.duration || 0;
  const endTime = startTime + duration;

  // Get animation settings (from customizations or layer defaults)
  const entranceAnim = customizations[layer.id]?.entrance_animation || layer.entrance_animation || 'none';
  const exitAnim = customizations[layer.id]?.exit_animation || layer.exit_animation || 'none';
  const entranceEasing = customizations[layer.id]?.entrance_easing || layer.entrance_easing || 'linear';
  const exitEasing = customizations[layer.id]?.exit_easing || layer.exit_easing || 'linear';
  const continuousAnim = customizations[layer.id]?.continuous_animation || layer.continuous_animation || 'none';
  const legacyAnimDuration = customizations[layer.id]?.animation_duration ?? layer.animation_duration ?? 0.5;
  const entranceAnimDuration = customizations[layer.id]?.entrance_animation_duration ?? layer.entrance_animation_duration ?? legacyAnimDuration;
  const exitAnimDuration = customizations[layer.id]?.exit_animation_duration ?? layer.exit_animation_duration ?? legacyAnimDuration;
  const loopDuration = customizations[layer.id]?.loop_duration || layer.loop_duration || 1.0;
  const continuousStartPercent = customizations[layer.id]?.continuous_start_percent ?? layer.continuous_start_percent ?? 0;
  const continuousEndPercent = customizations[layer.id]?.continuous_end_percent ?? layer.continuous_end_percent ?? 100;
  const kenBurnsEndScale = customizations[layer.id]?.ken_burns_end_scale ?? layer.ken_burns_end_scale ?? 1.05;

  // Check if layer is currently visible
  if (currentTime < startTime || currentTime > endTime) {
    return { opacity: 0, pointerEvents: 'none' };
  }

  const timeIntoLayer = currentTime - startTime;
  const timeUntilEnd = endTime - currentTime;
  const layerProgress = duration > 0 ? timeIntoLayer / duration : 0;

  let style = {};

  // Apply entrance animation
  if (entranceAnim !== 'none' && timeIntoLayer < entranceAnimDuration) {
    const progress = timeIntoLayer / entranceAnimDuration;
    style = {
      ...style,
      ...getEntranceAnimation(entranceAnim, applyAnimationEasing(progress, entranceEasing)),
    };
  }
  // Apply exit animation
  else if (exitAnim !== 'none' && timeUntilEnd < exitAnimDuration) {
    const progress = timeUntilEnd / exitAnimDuration;
    style = {
      ...style,
      ...getExitAnimation(exitAnim, getEasedExitProgress(progress, exitEasing)),
    };
  }

  // Apply continuous animation (if no entrance/exit currently active)
  if (continuousAnim !== 'none' &&
      (entranceAnim === 'none' || timeIntoLayer >= entranceAnimDuration) &&
      (exitAnim === 'none' || timeUntilEnd >= exitAnimDuration)) {
    const normalizedStart = Math.min(Math.max(continuousStartPercent / 100, 0), 1);
    const normalizedEnd = Math.min(Math.max(continuousEndPercent / 100, 0), 1);
    if (normalizedEnd > normalizedStart && layerProgress >= normalizedStart && layerProgress <= normalizedEnd) {
      const activeDuration = duration * (normalizedEnd - normalizedStart);
      const activeTime = timeIntoLayer - duration * normalizedStart;
      const segmentProgress = activeDuration > 0 ? activeTime / activeDuration : 0;
      const loopProgress = loopDuration > 0 ? ((Math.max(activeTime, 0) % loopDuration) / loopDuration) : 0;
      const progress = continuousAnim === 'ken_burns' ? segmentProgress : loopProgress;
      style = {
        ...style,
        ...getContinuousAnimation(
          continuousAnim,
          progress,
          kenBurnsEndScale
        ),
      };
    }
  }

  return style;
};

const SUBTLE_SLIDE_DISTANCE_PX = 24;

const getSlideFadeAnimation = (direction, progress) => {
  const distance = (1 - progress) * SUBTLE_SLIDE_DISTANCE_PX;

  switch (direction) {
    case 'left':
      return { opacity: progress, transform: `translateX(${-distance}px)` };
    case 'right':
      return { opacity: progress, transform: `translateX(${distance}px)` };
    case 'top':
      return { opacity: progress, transform: `translateY(${-distance}px)` };
    case 'bottom':
    default:
      return { opacity: progress, transform: `translateY(${distance}px)` };
  }
};

const getCircularWipeAnimation = (progress) => {
  const radius = progress * 150;
  return {
    clipPath: `circle(${radius}% at 50% 50%)`,
    WebkitClipPath: `circle(${radius}% at 50% 50%)`,
  };
};

const getEntranceAnimation = (type, progress) => {
  switch (type) {
    case 'fade':
      return { opacity: progress };
    case 'flash':
      return {
        filter: `contrast(${1 - progress}) brightness(${1 + progress}) saturate(${1 - progress})`,
      };
    case 'slide_fade_in':
    case 'slide_fade_from_bottom':
      return getSlideFadeAnimation('bottom', progress);
    case 'slide_fade_from_left':
      return getSlideFadeAnimation('left', progress);
    case 'slide_fade_from_right':
      return getSlideFadeAnimation('right', progress);
    case 'slide_fade_from_top':
      return getSlideFadeAnimation('top', progress);
    case 'circular_wipe':
      return getCircularWipeAnimation(progress);
    case 'slide_left':
      return { transform: `translateX(${(1 - progress) * 100}%)` };
    case 'slide_right':
      return { transform: `translateX(${-(1 - progress) * 100}%)` };
    case 'slide_up':
      return { transform: `translateY(${(1 - progress) * 100}%)` };
    case 'slide_down':
      return { transform: `translateY(${-(1 - progress) * 100}%)` };
    case 'text_wipe':
      return { '--text-wipe-progress': progress, '--text-wipe-exit': 0 };
    case 'wipe_left':
      return { clipPath: `inset(0 ${(1 - progress) * 100}% 0 0)`, WebkitClipPath: `inset(0 ${(1 - progress) * 100}% 0 0)` };
    case 'wipe_right':
      return { clipPath: `inset(0 0 0 ${(1 - progress) * 100}%)`, WebkitClipPath: `inset(0 0 0 ${(1 - progress) * 100}%)` };
    case 'wipe_up':
      return { clipPath: `inset(0 0 ${(1 - progress) * 100}% 0)`, WebkitClipPath: `inset(0 0 ${(1 - progress) * 100}% 0)` };
    case 'wipe_down':
      return { clipPath: `inset(${(1 - progress) * 100}% 0 0 0)`, WebkitClipPath: `inset(${(1 - progress) * 100}% 0 0 0)` };
    default:
      return {};
  }
};

const getExitAnimation = (type, progress) => {
  switch (type) {
    case 'fade':
      return { opacity: progress };
    case 'flash':
      return {
        filter: `contrast(${1 - progress}) brightness(${1 + progress}) saturate(${1 - progress})`,
      };
    case 'slide_fade_out':
    case 'slide_fade_to_top':
      return getSlideFadeAnimation('top', progress);
    case 'slide_fade_to_left':
      return getSlideFadeAnimation('left', progress);
    case 'slide_fade_to_right':
      return getSlideFadeAnimation('right', progress);
    case 'slide_fade_to_bottom':
      return getSlideFadeAnimation('bottom', progress);
    case 'circular_wipe':
      return getCircularWipeAnimation(progress);
    case 'slide_left':
      return { transform: `translateX(${-(1 - progress) * 100}%)` };
    case 'slide_right':
      return { transform: `translateX(${(1 - progress) * 100}%)` };
    case 'slide_up':
      return { transform: `translateY(${-(1 - progress) * 100}%)` };
    case 'slide_down':
      return { transform: `translateY(${(1 - progress) * 100}%)` };
    case 'text_wipe':
      return { '--text-wipe-progress': 1 - progress, '--text-wipe-exit': 1 };
    case 'wipe_left':
      return { clipPath: `inset(0 ${(1 - progress) * 100}% 0 0)`, WebkitClipPath: `inset(0 ${(1 - progress) * 100}% 0 0)` };
    case 'wipe_right':
      return { clipPath: `inset(0 0 0 ${(1 - progress) * 100}%)`, WebkitClipPath: `inset(0 0 0 ${(1 - progress) * 100}%)` };
    case 'wipe_up':
      return { clipPath: `inset(0 0 ${(1 - progress) * 100}% 0)`, WebkitClipPath: `inset(0 0 ${(1 - progress) * 100}% 0)` };
    case 'wipe_down':
      return { clipPath: `inset(${(1 - progress) * 100}% 0 0 0)`, WebkitClipPath: `inset(${(1 - progress) * 100}% 0 0 0)` };
    default:
      return {};
  }
};

const getKenBurnsMotion = (
  progress,
  kenBurnsEndScale = 1.05
) => {
  const clampedProgress = Math.min(Math.max(progress, 0), 1);
  const clampedEndScale = Math.min(Math.max(kenBurnsEndScale, 1), 2);
  const easedProgress = 0.5 - 0.5 * Math.cos(Math.PI * clampedProgress);
  const scale = 1 + (clampedEndScale - 1) * easedProgress;

  return {
    transform: `translate3d(0, 0, 0) scale(${scale})`,
    transformOrigin: 'center center',
    willChange: 'transform',
    backfaceVisibility: 'hidden',
  };
};

const getContinuousAnimation = (
  type,
  progress,
  kenBurnsEndScale = 1.05
) => {
  const twoPi = 2 * Math.PI;
  switch (type) {
    case 'bounce':
      const bounceOffset = Math.abs(Math.sin(progress * twoPi)) * 20;
      return { transform: `translateY(-${bounceOffset}px)` };
    case 'pulse':
      const pulseOpacity = 0.7 + Math.abs(Math.sin(progress * twoPi)) * 0.3;
      return { opacity: pulseOpacity };
    case 'rotate':
      const rotation = progress * 360;
      return { transform: `rotate(${rotation}deg)` };
    case 'ken_burns':
      return getKenBurnsMotion(progress, kenBurnsEndScale);
    default:
      return {};
  }
};

const API_URL = "/api";

const GOOGLE_FONTS = [
  "Arial",
  "Roboto",
  "Open Sans",
  "Montserrat",
  "Lato",
  "Oswald",
  "Raleway",
  "Poppins",
  "Playfair Display",
  "Merriweather",
  "Ubuntu",
  "Nunito",
  "PT Sans",
  "Bebas Neue",
  "Anton",
  "Pacifico",
  "Dancing Script",
  "Caveat",
  "Lobster",
  "Righteous",
  "Permanent Marker",
];

const QUICK_INSERT_EMOJIS = ["😃", "😂", "😉", "🥰", "😍", "🥳", "🥹", "😋", "😊", "😱", "🤤", "🤑"];
export default function EditorPage() {
  const location = useLocation();
  const { templateId } = useParams();
  const [searchParams] = useSearchParams();
  const sourceRenderId = searchParams.get("renderId");
  const navigate = useLocalizedNavigation();
  const { currentTemplate, fetchTemplate, setCurrentTemplate, isLoading } = useTemplateStore();
  const { createRender, fetchRender, pollRenderStatus } = useRenderStore();
  const { user, isAuthenticated } = useAuthStore();

  const [selectedLayerState, setSelectedLayer] = useState(null);
  const [isPreviewFullscreen, setIsPreviewFullscreen] = useState(false);
  const [isMobilePreview, setIsMobilePreview] = useState(() => typeof window !== "undefined" && window.innerWidth < 768);
  const [canvasPreviewScale, setCanvasPreviewScale] = useState(1 / 3);
  const [customizations, setCustomizations] = useState({});
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isRestoringRevision, setIsRestoringRevision] = useState(Boolean(sourceRenderId));
  const [aiGenerating, setAiGenerating] = useState(false);
  const [showPropertyPanel, setShowPropertyPanel] = useState(true);

  // Local text content state for fast typing
  const [localTextContent, setLocalTextContent] = useState("");
  const textUpdateTimerRef = useRef(null);
  const textContentInputRef = useRef(null);
  const [recordingState, setRecordingState] = useState({
    isOpen: false,
    status: "idle",
    layerId: null,
    layerName: "",
    duration: 0,
    remaining: 0,
  });
  const [cameraChoiceLayer, setCameraChoiceLayer] = useState(null);
  const recordingPreviewRef = useRef(null);
  const recordingStreamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordingChunksRef = useRef([]);
  const recordingTimeoutRef = useRef(null);
  const recordingIntervalRef = useRef(null);

  const canvasRef = useRef(null);
  const layerListItemRefs = useRef({});
  const animationRef = useRef(null);
  const videoRefs = useRef({});
  const videoSeekStateRef = useRef({});
  const currentPlanId = (user?.subscription_plan || "").toLowerCase();
  const allowedPlans = new Set(["starter", "growth", "pro", "agency", "daily-test"]);
  const hasRemainingExports = Number(user?.exports_used || 0) < Number(user?.export_limit || 0);
  const isDailyTestPlan = currentPlanId === "daily-test";
  const canEditTemplates = isAuthenticated && allowedPlans.has(currentPlanId) && (!isDailyTestPlan || hasRemainingExports);
  const customTimelineResult = useMemo(
    () => buildCustomTimeline(currentTemplate, customizations),
    [currentTemplate, customizations]
  );
  // Keep scene names and recording duration current after timeline changes.
  const selectedLayer = customTimelineResult.layers.find((layer) => layer.id === selectedLayerState?.id) || selectedLayerState;
  const editorTemplate = currentTemplate ? {
    ...currentTemplate,
    layers: customTimelineResult.layers,
    duration: customTimelineResult.timeline ? customTimelineResult.duration : currentTemplate.duration,
  } : null;
  const getClientReviewVideoLayer = () => editorTemplate?.layers?.find((layer) => layer.client_review_role === "video");
  const getClientReviewPreviewDuration = () => {
    const reviewVideoLayer = getClientReviewVideoLayer();
    if (!reviewVideoLayer) {
      return currentTemplate?.duration || 0;
    }
    const custom = customizations[reviewVideoLayer.id] || {};
    const sourceDuration = Number(reviewVideoLayer.source_duration || reviewVideoLayer.duration || currentTemplate?.duration || 0) || 0;
    const trimStart = Math.max(0, Number(custom.trim_start ?? reviewVideoLayer.trim_start ?? 0) || 0);
    const trimEnd = Math.max(0, Number(custom.trim_end ?? reviewVideoLayer.trim_end ?? 0) || 0);
    return Math.max(0.1, sourceDuration - trimStart - trimEnd);
  };
  const previewDuration = customTimelineResult.timeline ? customTimelineResult.duration : getClientReviewPreviewDuration();

  useEffect(() => {
    if (!currentTemplate || customizations[CUSTOM_TIMELINE_KEY]) return;
    const initial = customTimelineResult.timeline || getInitialCustomTimeline(currentTemplate);
    if (initial) setCustomizations((previous) => ({ ...previous, [CUSTOM_TIMELINE_KEY]: initial }));
  }, [currentTemplate, customizations, customTimelineResult.timeline]);

  // Open the first scene's media controls as soon as a Custom Group template
  // loads so the upload button is visible without an extra selection click.
  useEffect(() => {
    if (selectedLayer || !customTimelineResult.timeline?.groups?.length) return;
    const firstGroup = customTimelineResult.timeline.groups[0];
    const firstMedia = customTimelineResult.layers.find(
      (layer) => layer.id === `${firstGroup.id}:media`
    );
    if (firstMedia) setSelectedLayer(firstMedia);
  }, [customTimelineResult, selectedLayer]);

  useEffect(() => {
    if (!templateId || !canEditTemplates) return;

    let cancelled = false;
    const loadEditor = async () => {
      if (!sourceRenderId) {
        setCustomizations({});
        await fetchTemplate(templateId);
        return;
      }

      setIsRestoringRevision(true);
      const render = await fetchRender(sourceRenderId);
      if (cancelled) return;
      const snapshot = render?.editor_snapshot;
      if (!render || render.template_id !== templateId || !snapshot?.template) {
        toast.error(t("This video revision is no longer available to modify"));
        navigate(`/renders/${sourceRenderId}`);
        setIsRestoringRevision(false);
        return;
      }
      setCurrentTemplate(snapshot.template);
      setCustomizations(snapshot.customizations || {});
      setSelectedLayer(null);
      setCurrentTime(0);
      setIsRestoringRevision(false);
    };

    loadEditor();
    return () => {
      cancelled = true;
    };
  }, [templateId, sourceRenderId, fetchTemplate, fetchRender, setCurrentTemplate, canEditTemplates, navigate]);

  useEffect(() => {
    if (currentTemplate?.source === "client_review" && !selectedLayer) {
      const reviewVideoLayer = getClientReviewVideoLayer();
      if (reviewVideoLayer) {
        setSelectedLayer(reviewVideoLayer);
      }
    }
  }, [currentTemplate, selectedLayer]);

  const syncPreviewVideos = useCallback((timelineTime, shouldPlay) => {
    if (!currentTemplate) return;

    Object.entries(videoRefs.current).forEach(([layerId, videoEl]) => {
      if (!videoEl) return;

      const layer = editorTemplate.layers?.find(l => l.id === layerId);
      if (!layer) return;

      const seekState = videoSeekStateRef.current[layerId] || {};
      const seekVideo = (targetTime) => {
        const boundedTargetTime = Math.max(0, targetTime);
        const pendingDrift = seekState.pendingTime !== undefined
          ? Math.abs(seekState.pendingTime - boundedTargetTime)
          : Infinity;

        if (videoEl.seeking && pendingDrift < 0.04) {
          return;
        }

        seekState.pendingTime = boundedTargetTime;
        videoSeekStateRef.current[layerId] = seekState;

        if (typeof videoEl.fastSeek === "function") {
          videoEl.fastSeek(boundedTargetTime);
        } else {
          videoEl.currentTime = boundedTargetTime;
        }
      };

      const seekReversePreviewVideo = (targetTime) => {
        const boundedTargetTime = Math.max(0, targetTime);
        const now = performance.now();
        const lastReverseSeekAt = seekState.lastReverseSeekAt || 0;

        if (videoEl.seeking || now - lastReverseSeekAt < 90) {
          return;
        }

        seekState.lastReverseSeekAt = now;
        seekState.pendingTime = boundedTargetTime;
        videoSeekStateRef.current[layerId] = seekState;
        videoEl.currentTime = boundedTargetTime;
      };

      if (seekState.videoEl !== videoEl) {
        videoEl.addEventListener("seeked", () => {
          const currentSeekState = videoSeekStateRef.current[layerId];
          if (currentSeekState) {
            currentSeekState.pendingTime = undefined;
          }
        });
        seekState.videoEl = videoEl;
        videoSeekStateRef.current[layerId] = seekState;
      }

      const custom = customizations[layerId] || {};
      const mediaUrl = custom.media_url ?? layer.media_url;
      const effectiveLayer = { ...layer, ...custom };
      const trimStart = Math.max(0, Number(custom.trim_start ?? layer.trim_start ?? 0) || 0);
      const trimEnd = Math.max(0, Number(custom.trim_end ?? layer.trim_end ?? 0) || 0);
      const uploadedVideoStart = Math.max(0, Number(custom.uploaded_video_start ?? layer.uploaded_video_start ?? 0) || 0);
      const videoStartTime = timelineTime - layer.start_time;
      const layerDuration = Number(layer.duration) || 0;
      const isLayerActive = videoStartTime >= 0 && videoStartTime < layerDuration;

      videoEl.volume = Math.max(0, Math.min(1,
        (Number(custom.media_volume ?? layer.media_volume ?? 100) / 100)
        * getAnimationAudioGain(effectiveLayer, videoStartTime)
      ));

      if (!isLayerActive) {
        videoEl.pause();
        return;
      }

      const rawSourceDuration = Number.isFinite(videoEl.duration) && videoEl.duration > 0
        ? videoEl.duration
        : Number(custom.source_duration ?? layer.source_duration ?? 0);
      const sourceDuration = Math.max(0, rawSourceDuration || layerDuration);
      const reversePixversePreview = isPixverseReversePreviewLayer({ ...layer, ...custom }, mediaUrl);
      const targetTime = getPreviewVideoTargetTime({
        videoStartTime,
        layerDuration,
        sourceDuration,
        trimStart,
        trimEnd,
        uploadedVideoStart,
        freezeFirst: layer.freeze_first_frame,
        freezeLast: layer.freeze_last_frame,
        reverse: reversePixversePreview,
      });

      const drift = Math.abs(videoEl.currentTime - targetTime);

      if (Number.isFinite(targetTime)) {
        if (reversePixversePreview) {
          if (drift > 0.12) {
            seekReversePreviewVideo(targetTime);
          }
        } else {
          const needsFrameAccurateSeeking = layer.freeze_first_frame || layer.freeze_last_frame;
          const isFreeRunningPlayback = shouldPlay && !videoEl.paused && !needsFrameAccurateSeeking;
          const seekThreshold = isFreeRunningPlayback ? 0.75 : 0.08;

          if (drift > seekThreshold) {
            seekVideo(targetTime);
          }
        }
      }

      const safeUploadedVideoStart = Math.max(0, Math.min(uploadedVideoStart, sourceDuration));
      const safeTrimStart = Math.max(0, Math.min(trimStart + safeUploadedVideoStart, sourceDuration));
      const safeTrimEnd = Math.max(0, Math.min(trimEnd, Math.max(0, sourceDuration - safeTrimStart)));
      const availableDuration = Math.max(0.001, sourceDuration - safeTrimStart - safeTrimEnd);
      const freezeDuration = Math.max(0, layerDuration - availableDuration);
      const firstFreezeDuration = layer.freeze_first_frame && layer.freeze_last_frame
        ? freezeDuration / 2
        : layer.freeze_first_frame ? freezeDuration : 0;
      const isHoldingFirstFrame = layer.freeze_first_frame && videoStartTime < firstFreezeDuration;

      if (isHoldingFirstFrame || reversePixversePreview) {
        videoEl.pause();
      } else if (shouldPlay && videoEl.paused) {
        videoEl.play().catch(() => {});
      }
    });
  }, [currentTemplate, customizations]);

  useEffect(() => {
    if (!isPlaying && currentTemplate) {
      syncPreviewVideos(currentTime, false);
    }
  }, [currentTime, currentTemplate, customizations, isPlaying, syncPreviewVideos]);


  useEffect(() => {
    const updateMobilePreview = () => {
      setIsMobilePreview(window.innerWidth < 768);
    };

    updateMobilePreview();
    window.addEventListener("resize", updateMobilePreview);

    return () => {
      window.removeEventListener("resize", updateMobilePreview);
    };
  }, []);

  useEffect(() => {
    if (!canvasRef.current || !currentTemplate?.canvas_width) return undefined;

    const updateCanvasPreviewScale = () => {
      const renderedWidth = canvasRef.current?.getBoundingClientRect().width || 0;
      if (renderedWidth > 0) {
        setCanvasPreviewScale(renderedWidth / currentTemplate.canvas_width);
      }
    };

    updateCanvasPreviewScale();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateCanvasPreviewScale);
      return () => window.removeEventListener("resize", updateCanvasPreviewScale);
    }

    const observer = new ResizeObserver(updateCanvasPreviewScale);
    observer.observe(canvasRef.current);

    return () => observer.disconnect();
  }, [currentTemplate?.canvas_width, isMobilePreview, isPreviewFullscreen]);

  useEffect(() => {
    if (isPlaying && currentTemplate) {
      const startTime = Date.now() - currentTime * 1000;
      syncPreviewVideos(currentTime, true);

      const animate = () => {
        const elapsed = (Date.now() - startTime) / 1000;
        if (elapsed >= previewDuration) {
          setCurrentTime(0);
          setIsPlaying(false);
          // Pause all videos
          Object.values(videoRefs.current).forEach(v => v?.pause());
        } else {
          syncPreviewVideos(elapsed, true);
          setCurrentTime(elapsed);
          animationRef.current = requestAnimationFrame(animate);
        }
      };

      animationRef.current = requestAnimationFrame(animate);

      return () => {
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
        }
        // Pause videos when stopping
        Object.values(videoRefs.current).forEach(v => v?.pause());
      };
    } else {
      // Pause all videos when not playing
      Object.values(videoRefs.current).forEach(v => v?.pause());
    }
  }, [isPlaying, currentTemplate, previewDuration, syncPreviewVideos]);

  useEffect(() => {
    if ((currentTemplate?.source === "client_review" || customTimelineResult.timeline) && currentTime > previewDuration) {
      setCurrentTime(previewDuration);
    }
  }, [currentTemplate?.source, customTimelineResult.timeline, currentTime, previewDuration]);

  const handleLayerSelect = (layer) => {
    if (layer.editable) {
      setSelectedLayer(layer);
      setShowPropertyPanel(true);

      window.requestAnimationFrame(() => {
        layerListItemRefs.current[layer.id]?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      });
    }
  };

  const handleCustomization = (layerId, key, value) => {
    setCustomizations((prev) => {
      const existing = prev[layerId] || {};
      const baseLayer = editorTemplate?.layers?.find((layer) => layer.id === layerId) || {};
      const updatedLayer = {
        ...existing,
        [key]: value,
      };

      // 🎯 Auto-hide background when text is empty
      if (key === "content") {
        const content = String(value ?? "");
        const hasText = content.trim() !== "";

        // Save original background opacity once
        if (
          existing.originalBackgroundOpacity === undefined &&
          existing.backgroundOpacity !== undefined
        ) {
          updatedLayer.originalBackgroundOpacity = existing.backgroundOpacity;
        }

        const originalOpacity =
          existing.originalBackgroundOpacity ??
          existing.backgroundOpacity ??
          baseLayer.backgroundOpacity ??
          1;

        if (!hasText) {
          updatedLayer.backgroundOpacity = 0;
        } else {
          updatedLayer.backgroundOpacity = originalOpacity;
        }
      }

      return {
        ...prev,
        [layerId]: updatedLayer,
      };
   });
};

  const updateCustomTimeline = (updater) => {
    setCustomizations((previous) => {
      const timeline = previous[CUSTOM_TIMELINE_KEY] || getInitialCustomTimeline(currentTemplate);
      return { ...previous, [CUSTOM_TIMELINE_KEY]: updater(timeline) };
    });
  };

  const addCustomScene = () => {
    const definition = currentTemplate.layers.find((layer) => layer.type === "custom_group");
    const timeline = customizations[CUSTOM_TIMELINE_KEY] || getInitialCustomTimeline(currentTemplate);
    const newGroup = createGroup(definition, timeline.groups.length);
    updateCustomTimeline((timeline) => ({
      ...timeline,
      groups: [...timeline.groups, newGroup],
    }));
    // A newly added scene is ready for media immediately, matching the initial
    // scene experience and avoiding a second click before uploading.
    setSelectedLayer({
      ...definition,
      ...newGroup.media,
      id: `${newGroup.id}:media`,
      group_id: newGroup.id,
      type: "video",
      name: `Scene ${timeline.groups.length + 1} media`,
      duration: newGroup.duration,
      music_type: false,
      add_recording_capability: true,
    });
  };

  const deleteCustomScene = (groupId) => {
    const sceneLayers = customTimelineResult.layers.filter((layer) => layer.group_id === groupId);
    if (sceneLayers.some((layer) => layer.id === selectedLayer?.id)) {
      clearTimeout(textUpdateTimerRef.current);
      textUpdateTimerRef.current = null;
      setSelectedLayer(null);
    }
    if (sceneLayers.some((layer) => layer.id === cameraChoiceLayer?.id)) {
      setCameraChoiceLayer(null);
    }
    setIsPlaying(false);
    setCurrentTime(0);
    setCustomizations((previous) => removeCustomScene({
      ...previous,
      [CUSTOM_TIMELINE_KEY]: previous[CUSTOM_TIMELINE_KEY] || getInitialCustomTimeline(currentTemplate),
    }, groupId));
  };

  const setCustomSceneDuration = (groupId, duration) => updateCustomTimeline((timeline) => ({
    ...timeline,
    groups: timeline.groups.map((group) => group.id === groupId
      ? { ...group, duration: Math.max(0.1, Number(duration) || 4) }
      : group),
  }));

  const addCustomSceneLayer = (groupId, type) => {
    const id = `${groupId}:${type}:${Date.now()}`;
    const layer = {
      id, type, name: type === "text" ? "Text" : "Shape", editable: true,
      position_x: 10, position_y: type === "text" ? 42 : 30, width: 80,
      height: type === "text" ? 16 : 40, opacity: 1, rotation: 0, scale: 1,
      content: type === "text" ? "Your text" : null, shapeType: "square",
      backgroundColor: type === "text" ? "#ffffff" : "#14b8a6",
      backgroundOpacity: type === "text" ? 0 : 1, borderColor: "#000000", borderWidth: 0,
    };
    updateCustomTimeline((timeline) => ({ ...timeline, groups: timeline.groups.map((group) =>
      group.id === groupId ? { ...group, children: [...(group.children || []), layer] } : group) }));
    setSelectedLayer(layer);
  };

  const limitClientReviewCaption = (value) => {
    if (selectedLayer?.client_review_role !== "caption") {
      return value;
    }
    return String(value || "").split(/\r?\n/).slice(0, 2).join("\n");
  };

  // Debounced text update for fast typing
  const handleTextContentChange = (newContent) => {
    const limitedContent = limitClientReviewCaption(newContent);
    setLocalTextContent(limitedContent);

    // ✅ Instant preview update: triggers your existing auto-hide logic immediately
    if (selectedLayer?.id) {
      handleCustomization(selectedLayer.id, "content", limitedContent);
    }

    // ✅ Keep debounce if you want to reduce render calls elsewhere
    if (textUpdateTimerRef.current) {
      clearTimeout(textUpdateTimerRef.current);
    }

    textUpdateTimerRef.current = setTimeout(() => {
      if (selectedLayer?.id) {
        handleCustomization(selectedLayer.id, "content", limitedContent);
      }
   }, 500);
};

  const handleInsertEmoji = (emoji) => {
    const textareaEl = textContentInputRef.current;
    const currentContent = localTextContent || "";
    const selectionStart = textareaEl?.selectionStart ?? currentContent.length;
    const selectionEnd = textareaEl?.selectionEnd ?? currentContent.length;
    const nextContent = `${currentContent.slice(0, selectionStart)}${emoji}${currentContent.slice(selectionEnd)}`;
    const nextCursorPos = selectionStart + emoji.length;

    handleTextContentChange(nextContent);

    requestAnimationFrame(() => {
      if (textareaEl) {
        textareaEl.focus();
        textareaEl.setSelectionRange(nextCursorPos, nextCursorPos);
      }
    });
  };

  // Sync local text content when selected layer changes
  useEffect(() => {
    if (selectedLayer?.type === "text") {
      const custom = customizations[selectedLayer.id] || {};
      setLocalTextContent(
        Object.prototype.hasOwnProperty.call(custom, "content")
          ? (custom.content ?? "")
          : (selectedLayer.content ?? "")
      );
    }
  }, [selectedLayer?.id, selectedLayer?.content, customizations]);

  const clearRecordingTimers = () => {
    if (recordingTimeoutRef.current) {
      clearTimeout(recordingTimeoutRef.current);
      recordingTimeoutRef.current = null;
    }
    if (recordingIntervalRef.current) {
      clearInterval(recordingIntervalRef.current);
      recordingIntervalRef.current = null;
    }
  };

  const stopRecordingTracks = () => {
    recordingStreamRef.current?.getTracks().forEach((track) => track.stop());
    recordingStreamRef.current = null;
  };

  const resetRecording = () => {
    clearRecordingTimers();
    stopRecordingTracks();
    mediaRecorderRef.current = null;
    recordingChunksRef.current = [];
    if (recordingPreviewRef.current) {
      recordingPreviewRef.current.srcObject = null;
    }
    setRecordingState({
      isOpen: false,
      status: "idle",
      layerId: null,
      layerName: "",
      duration: 0,
      remaining: 0,
    });
  };

  // Cleanup timers, camera tracks, and text debounce on unmount
  useEffect(() => {
    return () => {
      if (textUpdateTimerRef.current) {
        clearTimeout(textUpdateTimerRef.current);
      }
      clearRecordingTimers();
      stopRecordingTracks();
    };
  }, []);

  useEffect(() => {
    if (recordingPreviewRef.current && recordingStreamRef.current) {
      recordingPreviewRef.current.srcObject = recordingStreamRef.current;
    }
  }, [recordingState.isOpen, recordingState.status]);

  const handleMediaUpload = async (layerId, file, options = {}) => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      if (options.normalizeVideo) {
        formData.append("normalize_video", "true");
      }

      const response = await axios.post("/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });


      // Backend returns "/api/files/{id}", use REACT_APP_BACKEND_URL to avoid /api/api/
      const mediaUrl = `${window.location.origin}${response.data.url}`;

      // Detect if uploaded file is audio, video, or image based on MIME type
      const isAudio = file.type.startsWith('audio/');
      const isVideo = file.type.startsWith('video/');
      const mediaType = isAudio ? 'audio' : isVideo ? 'video' : 'image';
      const sourceDuration = await getLocalMediaDuration(file);

      handleCustomization(layerId, "media_url", mediaUrl);
      handleCustomization(layerId, "media_type", mediaType);
      handleCustomization(layerId, "source_duration", sourceDuration && mediaType !== 'image' ? Number(sourceDuration.toFixed(3)) : null);
      handleCustomization(layerId, "uploaded_video_start", 0);
      toast.success(t("Media uploaded successfully"));
    } catch (error) {
      toast.error(t("Failed to upload media"));
    }
  };


  const handleRemoveMedia = (layerId) => {
    handleCustomization(layerId, "media_url", null);
    handleCustomization(layerId, "media_type", null);
    handleCustomization(layerId, "source_duration", null);
    handleCustomization(layerId, "uploaded_video_start", 0);
    toast.success(t("Media removed"));
  };

  const stopActiveRecording = ({ save = true } = {}) => {
    clearRecordingTimers();
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      recorder.onstop = save ? recorder.onstop : () => resetRecording();
      recorder.stop();
    } else {
      resetRecording();
    }
  };

  const handleRecordVideo = async (layer, facingMode = "environment") => {
    if (!layer) {
      return;
    }

    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      toast.error(t("Camera recording is not supported by this browser"));
      return;
    }

    setCameraChoiceLayer(null);

    const duration = Math.max(0.5, Number(layer.duration || 0) || 5);
    setRecordingState({
      isOpen: true,
      status: "requesting",
      layerId: layer.id,
      layerName: layer.name || t("Camera preview"),
      duration,
      remaining: duration,
    });

    try {
      const cameraConstraints = facingMode
        ? { facingMode: { ideal: facingMode } }
        : true;
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: cameraConstraints, audio: true });
      } catch (cameraError) {
        if (["NotAllowedError", "SecurityError", "PermissionDeniedError"].includes(cameraError?.name)) {
          throw cameraError;
        }
        console.warn("Preferred camera unavailable, falling back to default camera:", cameraError);
        stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      }
      recordingStreamRef.current = stream;
      if (recordingPreviewRef.current) {
        recordingPreviewRef.current.srcObject = stream;
      }

      const supportedMimeTypes = [
        "video/webm;codecs=vp9,opus",
        "video/webm;codecs=vp8,opus",
        "video/webm",
        "video/mp4",
      ];
      const mimeType = supportedMimeTypes.find((type) => MediaRecorder.isTypeSupported(type));
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      mediaRecorderRef.current = recorder;
      recordingChunksRef.current = [];

      recorder.ondataavailable = (event) => {
        if (event.data?.size > 0) {
          recordingChunksRef.current.push(event.data);
        }
      };

      recorder.onstop = async () => {
        const chunks = recordingChunksRef.current;
        stopRecordingTracks();
        setRecordingState((prev) => ({ ...prev, status: "uploading", remaining: 0 }));

        if (chunks.length > 0) {
          const blobType = recorder.mimeType || "video/webm";
          const extension = blobType.includes("mp4") ? "mp4" : "webm";
          const blob = new Blob(chunks, { type: blobType });
          const file = new File([blob], `recording-${Date.now()}.${extension}`, { type: blobType });
          await handleMediaUpload(layer.id, file, { normalizeVideo: true });
        }

        resetRecording();
      };

      recorder.start();
      setRecordingState((prev) => ({ ...prev, status: "recording", remaining: duration }));

      const startedAt = Date.now();
      recordingIntervalRef.current = setInterval(() => {
        const elapsed = (Date.now() - startedAt) / 1000;
        setRecordingState((prev) => ({ ...prev, remaining: Math.max(0, duration - elapsed) }));
      }, 200);

      recordingTimeoutRef.current = setTimeout(() => {
        stopActiveRecording({ save: true });
      }, duration * 1000);
    } catch (error) {
      console.error("Camera recording error:", error);
      toast.error(t("Camera permission denied or unavailable"));
      resetRecording();
    }
  };

  const getLayerMediaValue = (layerId, fallbackLayer, key) => {
    const layerCustomizations = customizations[layerId] || {};
    if (Object.prototype.hasOwnProperty.call(layerCustomizations, key)) {
      return layerCustomizations[key];
    }
    return fallbackLayer[key];
  };

  const handleAiGenerate = async (layer) => {
  if (!layer.pixverse_prompt) {
    toast.error(t("No AI prompt configured for this layer"));
    return;
  }

  const mediaUrl = getLayerMediaValue(layer.id, layer, "media_url");
  if (!mediaUrl) {
    toast.error(t("Please upload an image first"));
    return;
  }

  setAiGenerating(true);

  try {
    // 1) Fetch the uploaded image URL into a Blob
    const imgResp = await fetch(mediaUrl, { cache: "no-store" });
    if (!imgResp.ok) throw new Error("Could not fetch the uploaded image");
    const blob = await imgResp.blob();

    // Try to keep a reasonable filename/ext
    const contentType = blob.type || "image/jpeg";
    const ext =
      contentType === "image/png" ? "png" :
      contentType === "image/webp" ? "webp" :
      contentType === "image/gif" ? "gif" :
      "jpg";

    const file = new File([blob], `image.${ext}`, { type: contentType });

    // 2) Call backend PixVerse generate (NOTE: no /api prefix here)
    const form = new FormData();
    form.append("file", file);
    form.append("prompt", layer.pixverse_prompt);
    const pixverseDuration = Number(layer.pixverse_duration ?? 5);
    const clampedPixverseDuration = Math.max(1, Math.min(15, pixverseDuration || 5));
    form.append("duration", String(clampedPixverseDuration));

    const genResp = await axios.post("/pixverse/generate", form, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    const { video_id } = genResp.data;
    toast.info(t("Generating AI video..."));

    // 3) Poll status
    const maxAttempts = 60; // 60 * 2s = 120s
    for (let i = 0; i < maxAttempts; i++) {
      await new Promise((r) => setTimeout(r, 2000));

      const statusResp = await axios.get(`/pixverse/status/${video_id}`);
      const { status, url } = statusResp.data;

      if (status === "completed" && url) {
        // ✅ Replace same layer media
        handleCustomization(layer.id, "media_url", url);
        handleCustomization(layer.id, "media_type", "video");
        handleCustomization(layer.id, "ai_enabled", false);
        handleCustomization(layer.id, "pixverse_generated", true);

        toast.success(t("AI video ready, replaced the image in this layer"));
        return;
      }
    }

    toast.error(t("Timed out waiting for PixVerse video"));
  } catch (err) {
    console.error(err);
    toast.error(t("AI generation failed"));
  } finally {
    setAiGenerating(false);
  }
};

  const handleExport = async () => {
    if (user?.exports_used >= user?.export_limit) {
      toast.error(t("You reached your monthly export limit. Upgrade your plan or buy extra video exports."));
      navigate("/pricing");
      return;
    }

    setIsExporting(true);
    try {
      const render = await createRender(templateId, customizations, sourceRenderId);
      if (render) {
        toast.success(t("Export started!"));
        navigate(`/renders/${render.id}`);
      }
    } catch (error) {
      toast.error(t("Failed to start export"));
    } finally {
      setIsExporting(false);
    }
  };

  const getLayerStyle = (layer) => {
    const custom = customizations[layer.id] || {};
    const startPercent = (layer.start_time / (editorTemplate?.duration || 1)) * 100;
    const widthPercent = (layer.duration / (editorTemplate?.duration || 1)) * 100;

    const isVisible = currentTime >= layer.start_time && currentTime < layer.start_time + layer.duration;

    return {
      position: "absolute",
      left: `${getLayerPositionX(layer, custom)}%`,
      top: `${custom.position_y ?? layer.position_y}%`,
      width: `${custom.width ?? layer.width}%`,
      height: `${custom.height ?? layer.height}%`,
      opacity: isVisible ? (custom.opacity ?? layer.opacity) : 0,
      transform: `scale(${custom.scale ?? layer.scale}) rotate(${custom.rotation ?? layer.rotation}deg)`,
      zIndex: layer.z_index,
      transition: "opacity 0.3s ease",
    };
  };

  const getClipStyle = (layer) => {
    const startPercent = (layer.start_time / (editorTemplate?.duration || 1)) * 100;
    const widthPercent = (layer.duration / (editorTemplate?.duration || 1)) * 100;

    return {
      left: `${startPercent}%`,
      width: `${widthPercent}%`,
    };
  };

  if (!canEditTemplates) {
    const editorRedirect = `${stripLanguagePrefix(location.pathname)}${location.search}${location.hash}`;

    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white border border-slate-200 rounded-2xl p-8 text-center">
          <h1 className="text-xl font-bold mb-3">{t("You must subscribe or login to edit this template.")}</h1>
          <div className="flex items-center justify-center gap-3">
            <LocalizedLink
              to={`/login?redirect=${encodeURIComponent(editorRedirect)}`}
              state={{
                redirect: editorRedirect,
              }}
            >
              <Button variant="outline" className="rounded-full">{t("Login")}</Button>
            </LocalizedLink>
            <LocalizedLink to="/pricing">
              <Button className="rounded-full btn-primary">{t("Subscribe")}</Button>
            </LocalizedLink>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading || isRestoringRevision || !currentTemplate) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-teal-600 mx-auto mb-4" />
          <p className="text-slate-600">Loading template...</p>
        </div>
      </div>
    );
  }

  // Custom-scene layers are listed inside their scene card. This list is only
  // for layers the template author placed outside the Custom Group.
  const editableLayers = editorTemplate.layers?.filter((layer) => layer.editable && !layer.group_id) || [];
  const selectedLayerIsCustomSceneMedia = Boolean(
    selectedLayer?.group_id && selectedLayer?.id?.endsWith(":media")
  );
  const safeCanvasPreviewScale = canvasPreviewScale || (1 / 3);
  const previewScaleDivisor = 1 / safeCanvasPreviewScale;

  const selectedLayerProperties = selectedLayer ? (
              <div className="space-y-6">
                <div className="property-group">
                  <div className="property-group-title">{t("Layer Info")}</div>
                  <p className="text-sm font-medium">{t(selectedLayer.name)}</p>
                  <p className="text-xs text-slate-500 capitalize">
                    {selectedLayer.type === "text" ? t("text layer") : `${selectedLayer.type} layer`}
                  </p>
                </div>

                {selectedLayer.type === "shape" && (
                  <div className="property-group">
                    <div className="property-group-title">{t("Shape Settings")}</div>
                    <ShapeSettings
                      layer={selectedLayer}
                      custom={customizations[selectedLayer.id] || {}}
                      includeTransform
                      translate={t}
                      onChange={(key, value) => handleCustomization(selectedLayer.id, key, value)}
                    />
                  </div>
                )}

                {selectedLayer.type === "text" && (
                  <>
                    {/* Text Content */}
                    <div className="property-group">
                      <div className="property-group-title">{t("Text Content")}</div>
                      <Textarea
                        ref={textContentInputRef}
                        value={localTextContent}
                        onChange={(e) => handleTextContentChange(e.target.value)}
                        placeholder="Enter text..."
                        rows={selectedLayer.client_review_role === "caption" ? 2 : 3}
                        data-testid="text-content-input"
                      />
                      {selectedLayer.client_review_role === "caption" && (
                        <p className="text-xs text-slate-500 mt-1">{t("Optional. Maximum 2 lines; leave empty to hide this text.")}</p>
                      )}
                      <div className="mt-2 flex flex-wrap gap-2">
                        {QUICK_INSERT_EMOJIS.map((emoji) => (
                          <button
                            key={emoji}
                            type="button"
                            className="h-8 w-8 rounded border border-slate-200 bg-white text-lg leading-none hover:bg-slate-50"
                            onClick={() => handleInsertEmoji(emoji)}
                            aria-label={`Insert ${emoji}`}
                          >
                            {emoji}
                          </button>
                        ))}
                      </div>
                      <div className="mt-4 space-y-3">
                        {selectedLayer.position_presets_enabled !== false && (
                          <div>
                            <Label className="text-xs">{t("Position")}</Label>
                            <Select
                              value={getTextPositionPreset(customizations[selectedLayer.id]?.position_y ?? selectedLayer.position_y)}
                              onValueChange={(value) => handleCustomization(
                                selectedLayer.id,
                                "position_y",
                                TEXT_POSITION_PRESETS[value]
                              )}
                            >
                              <SelectTrigger className="h-8 text-sm" data-testid="text-position-select">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="top">{t("Top")}</SelectItem>
                                <SelectItem value="middle">{t("Middle")}</SelectItem>
                                <SelectItem value="bottom">{t("Bottom")}</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                        <div>
                          <Label className="text-xs">{t("Adjust position")}</Label>
                          <Input
                            type="number"
                            step={1}
                            value={customizations[selectedLayer.id]?.position_y ?? selectedLayer.position_y}
                            onChange={(event) => handleCustomization(
                              selectedLayer.id,
                              "position_y",
                              Number(event.target.value)
                            )}
                            className="h-8"
                            data-testid="text-position-adjust"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs">{t("Width %")}</Label>
                            <Input
                              type="number"
                              value={customizations[selectedLayer.id]?.width ?? selectedLayer.width}
                              onChange={(event) => handleCustomization(selectedLayer.id, "width", Number(event.target.value))}
                              className="h-8"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">{t("Height %")}</Label>
                            <Input
                              type="number"
                              value={customizations[selectedLayer.id]?.height ?? selectedLayer.height}
                              onChange={(event) => handleCustomization(selectedLayer.id, "height", Number(event.target.value))}
                              className="h-8"
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Text Styling */}
                    <Collapsible>
                      <CollapsibleTrigger data-testid="text-styling-toggle" className="property-group-title flex items-center justify-between w-full">
                        Text Styling
                        <ChevronRight className="w-4 h-4" />
                      </CollapsibleTrigger>
                      <CollapsibleContent className="property-group space-y-4 mt-3">
                        {/* Font Family */}
                        <div>
                          <Label className="text-xs">Font</Label>
                          <Select
                            value={customizations[selectedLayer.id]?.fontFamily || selectedLayer.fontFamily || "Arial"}
                            onValueChange={(v) => handleCustomization(selectedLayer.id, "fontFamily", v)}
                          >
                            <SelectTrigger data-testid="text-font-select" className="h-8 text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {GOOGLE_FONTS.map((font) => (
                                <SelectItem key={font} value={font} style={{ fontFamily: font }}>
                                  {font}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Text Style (Bold/Italic) */}
                        <div>
                          <Label className="text-xs mb-2 block">Text Style</Label>
                          <div className="flex gap-2">
                            <Button
                              type="button"
                              variant={(customizations[selectedLayer.id]?.fontWeight || selectedLayer.fontWeight) === "bold" ? "default" : "outline"}
                              size="sm"
                              className="flex-1"
                              onClick={() => handleCustomization(selectedLayer.id, "fontWeight",
                                (customizations[selectedLayer.id]?.fontWeight || selectedLayer.fontWeight) === "bold" ? "normal" : "bold"
                              )}
                            >
                              <Bold className="w-4 h-4" />
                            </Button>
                            <Button
                              type="button"
                              variant={(customizations[selectedLayer.id]?.fontStyle || selectedLayer.fontStyle) === "italic" ? "default" : "outline"}
                              size="sm"
                              className="flex-1"
                              onClick={() => handleCustomization(selectedLayer.id, "fontStyle",
                                (customizations[selectedLayer.id]?.fontStyle || selectedLayer.fontStyle) === "italic" ? "normal" : "italic"
                              )}
                            >
                              <Italic className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>

                        {/* Text Alignment */}
                        <div>
                          <Label className="text-xs mb-2 block">Alignment</Label>
                          <div className="grid grid-cols-4 gap-2">
                            <Button
                              type="button"
                              variant={(customizations[selectedLayer.id]?.textAlign || selectedLayer.textAlign || "center") === "left" ? "default" : "outline"}
                              size="sm"
                              onClick={() => handleCustomization(selectedLayer.id, "textAlign", "left")}
                            >
                              <AlignLeft className="w-4 h-4" />
                            </Button>
                            <Button
                              type="button"
                              variant={(customizations[selectedLayer.id]?.textAlign || selectedLayer.textAlign || "center") === "center" ? "default" : "outline"}
                              size="sm"
                              onClick={() => handleCustomization(selectedLayer.id, "textAlign", "center")}
                            >
                              <AlignCenter className="w-4 h-4" />
                            </Button>
                            <Button
                              type="button"
                              variant={(customizations[selectedLayer.id]?.textAlign || selectedLayer.textAlign || "center") === "right" ? "default" : "outline"}
                              size="sm"
                              onClick={() => handleCustomization(selectedLayer.id, "textAlign", "right")}
                            >
                              <AlignRight className="w-4 h-4" />
                            </Button>
                            <Button
                              type="button"
                              variant={(customizations[selectedLayer.id]?.textAlign || selectedLayer.textAlign || "center") === "justify" ? "default" : "outline"}
                              size="sm"
                              onClick={() => handleCustomization(selectedLayer.id, "textAlign", "justify")}
                            >
                              <AlignJustify className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>

                        {/* Text Color */}
                        <div>
                          <Label className="text-xs">Text Color</Label>
                          <div className="flex gap-2">
                            <Input
                              type="color"
                              value={customizations[selectedLayer.id]?.textColor || selectedLayer.textColor || "#000000"}
                              onChange={(e) => handleCustomization(selectedLayer.id, "textColor", e.target.value)}
                              className="h-8 w-16"
                            />
                            <Input
                              type="text"
                              data-testid="text-color-input"
                              value={customizations[selectedLayer.id]?.textColor || selectedLayer.textColor || "#000000"}
                              onChange={(e) => handleCustomization(selectedLayer.id, "textColor", e.target.value)}
                              placeholder="#000000"
                              className="h-8 flex-1"
                            />
                          </div>
                        </div>

                        {/* Text Stroke */}
                        <div>
                          <Label className="text-xs">Text Stroke Color</Label>
                          <div className="flex gap-2">
                            <Input
                              type="color"
                              value={customizations[selectedLayer.id]?.textStrokeColor || selectedLayer.textStrokeColor || "#000000"}
                              onChange={(e) => handleCustomization(selectedLayer.id, "textStrokeColor", e.target.value)}
                              className="h-8 w-16"
                            />
                            <Input
                              type="text"
                              value={customizations[selectedLayer.id]?.textStrokeColor || selectedLayer.textStrokeColor || "#000000"}
                              onChange={(e) => handleCustomization(selectedLayer.id, "textStrokeColor", e.target.value)}
                              placeholder="#000000"
                              className="h-8 flex-1"
                            />
                          </div>
                        </div>

                        <div>
                          <Label className="text-xs">Text Stroke Width</Label>
                          <div className="flex items-center gap-3">
                            <Slider
                              value={[customizations[selectedLayer.id]?.textStrokeWidth ?? selectedLayer.textStrokeWidth ?? 0]}
                              onValueChange={(v) => handleCustomization(selectedLayer.id, "textStrokeWidth", v[0])}
                              min={0}
                              max={20}
                              step={1}
                              className="flex-1"
                            />
                            <span className="text-xs text-slate-600 w-12">
                              {(customizations[selectedLayer.id]?.textStrokeWidth ?? selectedLayer.textStrokeWidth ?? 0).toFixed(0)}px
                            </span>
                          </div>
                        </div>

                        {/* Background Color */}
                        <div>
                          <Label className="text-xs">Background Color</Label>
                          <div className="flex gap-2">
                            <Input
                              type="color"
                              value={customizations[selectedLayer.id]?.backgroundColor || selectedLayer.backgroundColor || "#ffffff"}
                              onChange={(e) => handleCustomization(selectedLayer.id, "backgroundColor", e.target.value)}
                              className="h-8 w-16"
                            />
                            <Input
                              type="text"
                              data-testid="background-color-input"
                              value={customizations[selectedLayer.id]?.backgroundColor || selectedLayer.backgroundColor || "#ffffff"}
                              onChange={(e) => handleCustomization(selectedLayer.id, "backgroundColor", e.target.value)}
                              placeholder="#ffffff"
                              className="h-8 flex-1"
                            />
                          </div>
                        </div>

                        {/* Background Opacity */}
                        <div>
                          <Label className="text-xs">Background Opacity</Label>
                          <div className="flex items-center gap-3">
                            <Slider
                              data-testid="background-opacity"
                              value={[customizations[selectedLayer.id]?.backgroundOpacity ?? selectedLayer.backgroundOpacity ?? 0]}
                              onValueChange={(v) => handleCustomization(selectedLayer.id, "backgroundOpacity", v[0])}
                              min={0}
                              max={1}
                              step={0.1}
                              className="flex-1"
                            />
                            <span className="text-xs text-slate-600 w-8">
                              {((customizations[selectedLayer.id]?.backgroundOpacity ?? selectedLayer.backgroundOpacity ?? 0) * 100).toFixed(0)}%
                            </span>
                          </div>
                        </div>

                        {/* Background Border Color */}
                        <div>
                          <Label className="text-xs">Border Color</Label>
                          <div className="flex gap-2">
                            <Input
                              type="color"
                              value={customizations[selectedLayer.id]?.borderColor || selectedLayer.borderColor || "#000000"}
                              onChange={(e) => handleCustomization(selectedLayer.id, "borderColor", e.target.value)}
                              className="h-8 w-16"
                            />
                            <Input
                              type="text"
                              value={customizations[selectedLayer.id]?.borderColor || selectedLayer.borderColor || "#000000"}
                              onChange={(e) => handleCustomization(selectedLayer.id, "borderColor", e.target.value)}
                              placeholder="#000000"
                              className="h-8 flex-1"
                            />
                          </div>
                        </div>

                        {/* Background Border Width */}
                        <div>
                          <Label className="text-xs">Border Width</Label>
                          <div className="flex items-center gap-3">
                            <Slider
                              value={[customizations[selectedLayer.id]?.borderWidth ?? selectedLayer.borderWidth ?? 0]}
                              onValueChange={(v) => handleCustomization(selectedLayer.id, "borderWidth", v[0])}
                              min={0}
                              max={20}
                              step={1}
                              className="flex-1"
                            />
                            <span className="text-xs text-slate-600 w-12">
                              {(customizations[selectedLayer.id]?.borderWidth ?? selectedLayer.borderWidth ?? 0).toFixed(0)}px
                            </span>
                          </div>
                        </div>

                        {/* Line Spacing */}
                        <div>
                          <Label className="text-xs">Line Spacing</Label>
                          <div className="flex items-center gap-3">
                            <Slider
                              value={[customizations[selectedLayer.id]?.lineHeight || selectedLayer.lineHeight || 1.5]}
                              onValueChange={(v) => handleCustomization(selectedLayer.id, "lineHeight", v[0])}
                              min={0.8}
                              max={3}
                              step={0.1}
                              className="flex-1"
                            />
                            <span className="text-xs text-slate-600 w-12">
                              {(customizations[selectedLayer.id]?.lineHeight || selectedLayer.lineHeight || 1.5).toFixed(1)}
                            </span>
                          </div>
                        </div>

                        {/* Border Radius */}
                        <div>
                          <Label className="text-xs">Corner Radius (px)</Label>
                          <Input data-testid="text-corner-radius"
                            type="number"
                            value={customizations[selectedLayer.id]?.borderRadius || selectedLayer.borderRadius || 0}
                            onChange={(e) => handleCustomization(selectedLayer.id, "borderRadius", Number(e.target.value))}
                            min={0}
                            max={100}
                            step={1}
                            className="h-8"
                            placeholder="0"
                          />
                          <p className="text-xs text-slate-500 mt-1">
                            0 = Sharp corners, Higher = Rounder corners
                          </p>
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  </>
                )}

                {(selectedLayer.type === "image" || selectedLayer.type === "video") && (
                  <div className="property-group">
                    <div className="property-group-title">Media</div>
                    {selectedLayer.client_review_role !== "video" && (
                      <label className="block">
                        <div className="border-2 border-dashed border-slate-200 rounded-lg p-4 text-center cursor-pointer hover:border-violet-300 transition-colors">
                          <Upload className="w-6 h-6 mx-auto mb-2 text-slate-400" />
                          <span className="text-sm text-slate-600">
                            {selectedLayer.client_review_role === "logo" ? t("Upload optional logo") : t(selectedLayer.type === "image" ? "Upload image" : "Upload image, video, or mp3")}
                          </span>
                        </div>
                        <input
                          type="file"
                          accept={selectedLayer.type === "image" ? "image/*" : "image/*,video/*,audio/mpeg,.mp3"}
                          className="hidden"
                          onChange={(e) => {
                            if (e.target.files?.[0]) {
                              handleMediaUpload(selectedLayer.id, e.target.files[0]);
                            }
                          }}
                          data-testid="media-upload-input"
                        />
                      </label>
                    )}
                    {selectedLayer.client_review_role === "logo" && (
                      <p className="text-xs text-slate-500 mt-2">{t("Optional. If no logo is uploaded, nothing will appear in the final video. Uploaded logos are limited to the top-right 150px area.")}</p>
                    )}

                    {selectedLayer.type === "video" && selectedLayer.client_review_role !== "video" && getLayerMediaValue(selectedLayer.id, selectedLayer, "media_url") && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="w-full mt-3"
                        onClick={() => handleRemoveMedia(selectedLayer.id)}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        {t("Remove image or video")}
                      </Button>
                    )}

                    {selectedLayer.type === "video" && selectedLayer.music_type && (
                      <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
                        <div className="mb-2 flex items-center justify-between">
                          <Label className="text-xs font-medium text-slate-700">{t("Volume")}</Label>
                          <span className="text-xs text-slate-600">{customizations[selectedLayer.id]?.media_volume ?? selectedLayer.media_volume ?? 100}%</span>
                        </div>
                        <Slider
                          value={[customizations[selectedLayer.id]?.media_volume ?? selectedLayer.media_volume ?? 100]}
                          onValueChange={(v) => handleCustomization(selectedLayer.id, "media_volume", v[0])}
                          min={0}
                          max={100}
                          step={1}
                          className="flex-1"
                        />
                        <p className="text-xs text-slate-500 mt-2">{t("Adjusts the music volume in the preview and final export.")}</p>
                      </div>
                    )}

                    {selectedLayer.type === "video" && selectedLayer.client_review_role !== "video" && selectedLayer.add_recording_capability && (
                      <div className="mt-3 space-y-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="w-full"
                          onClick={() => setCameraChoiceLayer(selectedLayer)}
                          disabled={recordingState.isOpen}
                        >
                          <Camera className="w-4 h-4 mr-2" />
                          {t("Record video with your camera")}
                        </Button>
                        <p className="text-xs text-slate-500">
                          {t("Start recording with your camera and it will stop automatically at the end of the clip")}
                        </p>
                      </div>
                    )}

                    {selectedLayer.pixverse_prompt && (
                      <Button
                        className="w-full mt-3 btn-ai rounded-lg"
                        onClick={() => handleAiGenerate(selectedLayer)}
                        disabled={aiGenerating}
                        data-testid="ai-generate-btn"
                      >
                        {aiGenerating ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Sparkles className="w-4 h-4 mr-2" />
                        )}
                        {t("Generate AI Video")}
                      </Button>
                    )}

                    {selectedLayer.client_review_role !== "video" && !selectedLayerIsCustomSceneMedia &&
                      getLayerMediaValue(selectedLayer.id, selectedLayer, "media_url") &&
                      getLayerMediaValue(selectedLayer.id, selectedLayer, "media_type") === "video" && (() => {
                        const sourceDuration = Number(getLayerMediaValue(selectedLayer.id, selectedLayer, "source_duration") || 0);
                        const layerDuration = Number(selectedLayer.duration || 0);
                        const maxStart = Math.max(0, sourceDuration - Math.max(0.1, layerDuration));
                        const isUploadedVideoShorter = sourceDuration > 0 && layerDuration > 0 && sourceDuration < layerDuration;
                        const currentStart = Math.min(
                          maxStart,
                          Math.max(0, Number(getLayerMediaValue(selectedLayer.id, selectedLayer, "uploaded_video_start") || 0))
                        );

                        return (
                          <div className="mt-4 space-y-3 rounded-lg border border-slate-200 p-3 bg-slate-50">
                            <Label className="text-xs font-medium text-slate-700">{t("Begin uploaded video at (seconds)")}</Label>
                            <Input
                              type="number"
                              min="0"
                              max={maxStart}
                              step="0.1"
                              value={currentStart}
                              onChange={(e) => handleCustomization(
                                selectedLayer.id,
                                "uploaded_video_start",
                                Math.min(maxStart, Math.max(0, Number(e.target.value) || 0))
                              )}
                              className="h-8"
                            />
                            {sourceDuration > 0 && layerDuration > 0 ? (
                              <p className="text-xs text-slate-500">
                                <span className={isUploadedVideoShorter ? "font-medium text-red-600" : undefined}>
                                  {t("Your uploaded video is")} {sourceDuration.toFixed(1)}s
                                </span>{" "}
                                {t("and the layer is")} {layerDuration.toFixed(1)}s. {t("Move forward to show a specific portion inside this layer.")}
                              </p>
                            ) : (
                              <p className="text-xs text-slate-500">
                                {t("Move the uploaded video forward to choose which part appears inside this layer.")}
                              </p>
                            )}
                          </div>
                        );
                      })()}

                    {(selectedLayer.client_review_role === "video" || selectedLayerIsCustomSceneMedia) &&
                      getLayerMediaValue(selectedLayer.id, selectedLayer, "media_url") &&
                      getLayerMediaValue(selectedLayer.id, selectedLayer, "media_type") === "video" && (
                      <div className="mt-4 space-y-3 rounded-lg border border-slate-200 p-3 bg-slate-50">
                        <Label className="text-xs font-medium text-slate-700">
                          {t(selectedLayerIsCustomSceneMedia ? "Trim video" : "Trim recorded video")}
                        </Label>
                        <div>
                          <Label className="text-xs mb-1 block text-slate-600">{t("Remove from beginning (seconds)")}</Label>
                          <Input
                            type="number"
                            min="0"
                            max={Math.max(0, Number(getLayerMediaValue(selectedLayer.id, selectedLayer, "source_duration") || selectedLayer.duration || 30) - Number(customizations[selectedLayer.id]?.trim_end ?? selectedLayer.trim_end ?? 0) - 0.1)}
                            step="0.1"
                            value={customizations[selectedLayer.id]?.trim_start ?? selectedLayer.trim_start ?? 0}
                            onChange={(e) => {
                              const sourceDuration = Number(getLayerMediaValue(selectedLayer.id, selectedLayer, "source_duration") || selectedLayer.duration || 30);
                              const trimEnd = Number(customizations[selectedLayer.id]?.trim_end ?? selectedLayer.trim_end ?? 0);
                              handleCustomization(selectedLayer.id, "trim_start", Math.min(
                                Math.max(0, sourceDuration - trimEnd - 0.1),
                                Math.max(0, Number(e.target.value) || 0)
                              ));
                            }}
                            className="h-8"
                          />
                        </div>
                        <div>
                          <Label className="text-xs mb-1 block text-slate-600">{t("Remove from end (seconds)")}</Label>
                          <Input
                            type="number"
                            min="0"
                            max={Math.max(0, Number(getLayerMediaValue(selectedLayer.id, selectedLayer, "source_duration") || selectedLayer.duration || 30) - Number(customizations[selectedLayer.id]?.trim_start ?? selectedLayer.trim_start ?? 0) - 0.1)}
                            step="0.1"
                            value={customizations[selectedLayer.id]?.trim_end ?? selectedLayer.trim_end ?? 0}
                            onChange={(e) => {
                              const sourceDuration = Number(getLayerMediaValue(selectedLayer.id, selectedLayer, "source_duration") || selectedLayer.duration || 30);
                              const trimStart = Number(customizations[selectedLayer.id]?.trim_start ?? selectedLayer.trim_start ?? 0);
                              handleCustomization(selectedLayer.id, "trim_end", Math.min(
                                Math.max(0, sourceDuration - trimStart - 0.1),
                                Math.max(0, Number(e.target.value) || 0)
                              ));
                            }}
                            className="h-8"
                          />
                        </div>
                        {!selectedLayerIsCustomSceneMedia && (
                          <p className="text-xs text-slate-500">{t("Example: use 1.0 to remove the first second while the client adjusts their phone.")}</p>
                        )}
                      </div>
                    )}

                    {/* Object Position - only show if media is uploaded */}
                    {getLayerMediaValue(selectedLayer.id, selectedLayer, "media_url") && (
                      <div className="mt-4 space-y-3">
                        <Label className="text-xs font-medium text-slate-700">{t("Media Position")}</Label>

                        <div>
                          <Label className="text-xs mb-2 block text-slate-600">Fit</Label>
                          <Select
                            value={getLayerMediaValue(selectedLayer.id, selectedLayer, "media_fit") || "cover"}
                            onValueChange={(value) => handleCustomization(selectedLayer.id, "media_fit", value)}
                          >
                            <SelectTrigger className="h-8">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="cover">Cover (crop to fill)</SelectItem>
                              <SelectItem value="contain">Contain (no crop)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div>
                          <Label className="text-xs mb-2 block text-slate-600">Horizontal</Label>
                          <div className="flex items-center gap-3">
                            <Slider data-testid="photo-position-x"
                              value={[customizations[selectedLayer.id]?.objectPositionX ?? selectedLayer.objectPositionX ?? 50]}
                              onValueChange={(v) => handleCustomization(selectedLayer.id, "objectPositionX", v[0])}
                              min={0}
                              max={100}
                              step={1}
                              className="flex-1"
                            />
                            <span className="text-xs text-slate-600 w-12">
                              {customizations[selectedLayer.id]?.objectPositionX ?? selectedLayer.objectPositionX ?? 50}%
                            </span>
                          </div>
                          <p className="text-xs text-slate-500 mt-1">
                            0% = Left, 50% = Center, 100% = Right
                          </p>
                        </div>

                        <div>
                          <Label className="text-xs mb-2 block text-slate-600">Vertical</Label>
                          <div className="flex items-center gap-3">
                            <Slider data-testid="photo-position-y"
                              value={[customizations[selectedLayer.id]?.objectPositionY ?? selectedLayer.objectPositionY ?? 50]}
                              onValueChange={(v) => handleCustomization(selectedLayer.id, "objectPositionY", v[0])}
                              min={0}
                              max={100}
                              step={1}
                              className="flex-1"
                            />
                            <span className="text-xs text-slate-600 w-12">
                              {customizations[selectedLayer.id]?.objectPositionY ?? selectedLayer.objectPositionY ?? 50}%
                            </span>
                          </div>
                          <p className="text-xs text-slate-500 mt-1">
                            0% = Top, 50% = Center, 100% = Bottom
                          </p>
                        </div>

                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full"
                          onClick={() => {
                            handleCustomization(selectedLayer.id, "objectPositionX", 50);
                            handleCustomization(selectedLayer.id, "objectPositionY", 50);
                          }}
                        >
                          {t("Reset to Center")}
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {/* Animations Section - Show for ALL layers */}
                <Collapsible data-testid="animation-settings">
                  <CollapsibleTrigger data-testid="animation-toggle" className="property-group-title flex items-center justify-between w-full">
                    <span className="flex items-center gap-2">
                      <Zap className="w-4 h-4 text-purple-500" />
                      {t("Animations")}
                    </span>
                    <ChevronRight className="w-4 h-4" />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="property-group space-y-4 mt-3">
                    {/* Entrance Animation */}
                    <div>
                      <Label className="text-xs mb-2 block">{t("Entrance Animation")}</Label>
                      <Select
                        value={customizations[selectedLayer.id]?.entrance_animation || selectedLayer.entrance_animation || "none"}
                        onValueChange={(v) => handleCustomization(selectedLayer.id, "entrance_animation", v)}
                      >
                        <SelectTrigger className="h-9 text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          <SelectItem value="fade">Fade In</SelectItem>
                          <SelectItem value="slide_fade_from_left">Slide &amp; Fade from Left</SelectItem>
                          <SelectItem value="slide_fade_from_right">Slide &amp; Fade from Right</SelectItem>
                          <SelectItem value="slide_fade_from_top">Slide &amp; Fade from Top</SelectItem>
                          <SelectItem value="slide_fade_from_bottom">Slide &amp; Fade from Bottom</SelectItem>
                          <SelectItem value="circular_wipe">Circular Wipe In</SelectItem>
                          <SelectItem value="flash">Camera Flash In</SelectItem>
                          <SelectItem value="slide_left">Slide from Right</SelectItem>
                          <SelectItem value="slide_right">Slide from Left</SelectItem>
                          <SelectItem value="slide_up">Slide from Bottom</SelectItem>
                          <SelectItem value="slide_down">Slide from Top</SelectItem>
                          {selectedLayer.type === "text" && <SelectItem value="text_wipe">Text wipe</SelectItem>}
                          <SelectItem value="wipe_left">Wipe from Left</SelectItem>
                          <SelectItem value="wipe_right">Wipe from Right</SelectItem>
                          <SelectItem value="wipe_up">Wipe from Top</SelectItem>
                          <SelectItem value="wipe_down">Wipe from Bottom</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {(customizations[selectedLayer.id]?.entrance_animation || selectedLayer.entrance_animation) !== "none" && (
                      <>
                        <div>
                          <Label className="text-xs mb-2 block">Entrance Easing</Label>
                          <Select
                            value={customizations[selectedLayer.id]?.entrance_easing || selectedLayer.entrance_easing || "linear"}
                            onValueChange={(v) => handleCustomization(selectedLayer.id, "entrance_easing", v)}
                          >
                            <SelectTrigger className="h-9 text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="linear">Linear</SelectItem>
                              <SelectItem value="ease_in">Ease In</SelectItem>
                              <SelectItem value="ease_out">Ease Out</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs mb-2 block">Entrance Animation Duration (s)</Label>
                          <div className="flex items-center gap-3">
                            <Slider
                              value={[customizations[selectedLayer.id]?.entrance_animation_duration ?? selectedLayer.entrance_animation_duration ?? customizations[selectedLayer.id]?.animation_duration ?? selectedLayer.animation_duration ?? 0.5]}
                              onValueChange={(v) => handleCustomization(selectedLayer.id, "entrance_animation_duration", v[0])}
                              min={0.1}
                              max={3}
                              step={0.1}
                              className="flex-1"
                            />
                            <span className="text-xs text-slate-600 w-12">
                              {(customizations[selectedLayer.id]?.entrance_animation_duration ?? selectedLayer.entrance_animation_duration ?? customizations[selectedLayer.id]?.animation_duration ?? selectedLayer.animation_duration ?? 0.5).toFixed(1)}s
                            </span>
                          </div>
                        </div>
                      </>
                    )}

                    {/* Exit Animation */}
                    <div>
                      <Label className="text-xs mb-2 block">{t("Exit Animation")}</Label>
                      <Select
                        value={customizations[selectedLayer.id]?.exit_animation || selectedLayer.exit_animation || "none"}
                        onValueChange={(v) => handleCustomization(selectedLayer.id, "exit_animation", v)}
                      >
                        <SelectTrigger className="h-9 text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          <SelectItem value="fade">Fade Out</SelectItem>
                          <SelectItem value="slide_fade_to_left">Slide &amp; Fade to Left</SelectItem>
                          <SelectItem value="slide_fade_to_right">Slide &amp; Fade to Right</SelectItem>
                          <SelectItem value="slide_fade_to_top">Slide &amp; Fade to Top</SelectItem>
                          <SelectItem value="slide_fade_to_bottom">Slide &amp; Fade to Bottom</SelectItem>
                          <SelectItem value="circular_wipe">Circular Wipe Out</SelectItem>
                          <SelectItem value="flash">Camera Flash Out</SelectItem>
                          <SelectItem value="slide_left">Slide to Left</SelectItem>
                          <SelectItem value="slide_right">Slide to Right</SelectItem>
                          <SelectItem value="slide_up">Slide to Top</SelectItem>
                          <SelectItem value="slide_down">Slide to Bottom</SelectItem>
                          {selectedLayer.type === "text" && <SelectItem value="text_wipe">Text wipe</SelectItem>}
                          <SelectItem value="wipe_left">Wipe to Left</SelectItem>
                          <SelectItem value="wipe_right">Wipe to Right</SelectItem>
                          <SelectItem value="wipe_up">Wipe to Top</SelectItem>
                          <SelectItem value="wipe_down">Wipe to Bottom</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {(customizations[selectedLayer.id]?.exit_animation || selectedLayer.exit_animation) !== "none" && (
                      <>
                        <div>
                          <Label className="text-xs mb-2 block">Exit Easing</Label>
                          <Select
                            value={customizations[selectedLayer.id]?.exit_easing || selectedLayer.exit_easing || "linear"}
                            onValueChange={(v) => handleCustomization(selectedLayer.id, "exit_easing", v)}
                          >
                            <SelectTrigger className="h-9 text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="linear">Linear</SelectItem>
                              <SelectItem value="ease_in">Ease In</SelectItem>
                              <SelectItem value="ease_out">Ease Out</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs mb-2 block">Exit Animation Duration (s)</Label>
                          <div className="flex items-center gap-3">
                            <Slider
                              value={[customizations[selectedLayer.id]?.exit_animation_duration ?? selectedLayer.exit_animation_duration ?? customizations[selectedLayer.id]?.animation_duration ?? selectedLayer.animation_duration ?? 0.5]}
                              onValueChange={(v) => handleCustomization(selectedLayer.id, "exit_animation_duration", v[0])}
                              min={0.1}
                              max={3}
                              step={0.1}
                              className="flex-1"
                            />
                            <span className="text-xs text-slate-600 w-12">
                              {(customizations[selectedLayer.id]?.exit_animation_duration ?? selectedLayer.exit_animation_duration ?? customizations[selectedLayer.id]?.animation_duration ?? selectedLayer.animation_duration ?? 0.5).toFixed(1)}s
                            </span>
                          </div>
                        </div>
                      </>
                    )}

                    {/* Continuous Animation */}
                    <div>
                      <Label className="text-xs mb-2 block">{t("Continuous Animation")}</Label>
                      <Select
                        value={customizations[selectedLayer.id]?.continuous_animation || selectedLayer.continuous_animation || "none"}
                        onValueChange={(v) => handleCustomization(selectedLayer.id, "continuous_animation", v)}
                      >
                        <SelectTrigger className="h-9 text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          <SelectItem value="bounce">Bounce</SelectItem>
                          <SelectItem value="pulse">Pulse</SelectItem>
                          <SelectItem value="rotate">Rotate</SelectItem>
                          <SelectItem value="ken_burns">Ken Burns</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Loop Duration for Continuous Animation */}
                    {(customizations[selectedLayer.id]?.continuous_animation || selectedLayer.continuous_animation) !== "none" &&
                     (customizations[selectedLayer.id]?.continuous_animation || selectedLayer.continuous_animation) && (
                      <>
                        {(customizations[selectedLayer.id]?.continuous_animation || selectedLayer.continuous_animation) !== "ken_burns" && (
                          <div>
                            <Label className="text-xs mb-2 block">Loop Duration (s)</Label>
                            <div className="flex items-center gap-3">
                              <Slider
                                value={[customizations[selectedLayer.id]?.loop_duration || selectedLayer.loop_duration || 1.0]}
                                onValueChange={(v) => handleCustomization(selectedLayer.id, "loop_duration", v[0])}
                                min={0.5}
                                max={5}
                                step={0.1}
                                className="flex-1"
                              />
                              <span className="text-xs text-slate-600 w-12">
                                {(customizations[selectedLayer.id]?.loop_duration || selectedLayer.loop_duration || 1.0).toFixed(1)}s
                              </span>
                            </div>
                          </div>
                        )}
                        {((customizations[selectedLayer.id]?.continuous_animation || selectedLayer.continuous_animation) === "ken_burns") && (
                          <div>
                            <Label className="text-xs mb-2 block">Ken Burns End Scale</Label>
                            <Input
                              type="number"
                              min={1}
                              max={2}
                              step={0.01}
                              value={customizations[selectedLayer.id]?.ken_burns_end_scale ?? selectedLayer.ken_burns_end_scale ?? 1.05}
                              onChange={(e) => handleCustomization(selectedLayer.id, "ken_burns_end_scale", Number(e.target.value))}
                              className="h-9"
                            />
                          </div>
                        )}
                        <div>
                          <Label className="text-xs mb-2 block">Continuous Start / End (%)</Label>
                          <div className="grid grid-cols-2 gap-2">
                            <Input
                              type="number"
                              min={0}
                              max={100}
                              value={customizations[selectedLayer.id]?.continuous_start_percent ?? selectedLayer.continuous_start_percent ?? 0}
                              onChange={(e) => handleCustomization(selectedLayer.id, "continuous_start_percent", Number(e.target.value))}
                              className="h-9"
                            />
                            <Input
                              type="number"
                              min={0}
                              max={100}
                              value={customizations[selectedLayer.id]?.continuous_end_percent ?? selectedLayer.continuous_end_percent ?? 100}
                              onChange={(e) => handleCustomization(selectedLayer.id, "continuous_end_percent", Number(e.target.value))}
                              className="h-9"
                            />
                          </div>
                        </div>
                      </>
                    )}

                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                      <p className="text-xs text-purple-700">
                        💡 Animations will be applied when rendering the final video
                      </p>
                    </div>
                  </CollapsibleContent>
                </Collapsible>

              </div>
            ) : (
              <div className="text-center py-8">
                <Eye className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                <p className="text-sm text-slate-500">Select an editable layer to view properties</p>
              </div>
            );

  return (
    <>
    <Dialog open={Boolean(cameraChoiceLayer)} onOpenChange={(open) => {
      if (!open) {
        setCameraChoiceLayer(null);
      }
    }}>
      <DialogContent className="max-w-sm bg-white">
        <DialogHeader>
          <DialogTitle>{t("Choose camera")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <p className="text-sm text-slate-600">
            {t("Which camera would you like to use for this recording?")}
          </p>
          <Button
            type="button"
            className="w-full btn-primary rounded-lg"
            onClick={() => handleRecordVideo(cameraChoiceLayer, "environment")}
          >
            <Camera className="w-4 h-4 mr-2" />
            {t("Use rear camera")}
          </Button>
          <Button
            type="button"
            variant="outline"
            className="w-full rounded-lg"
            onClick={() => handleRecordVideo(cameraChoiceLayer, "user")}
          >
            <Camera className="w-4 h-4 mr-2" />
            {t("Use front camera")}
          </Button>
          <p className="text-xs text-slate-500">
            {t("Rear camera is recommended when filming something in front of you.")}
          </p>
        </div>
      </DialogContent>
    </Dialog>
    <Dialog open={recordingState.isOpen} onOpenChange={(open) => {
      if (!open && recordingState.status !== "uploading") {
        stopActiveRecording({ save: false });
      }
    }}>
      <DialogContent className="max-w-[80vw] h-[80vh] p-0 overflow-hidden bg-black border-0">
        <DialogHeader className="sr-only">
          <DialogTitle>{t("Camera preview")}</DialogTitle>
        </DialogHeader>
        <div className="relative w-full h-full bg-black">
          <video
            ref={recordingPreviewRef}
            autoPlay
            muted
            playsInline
            className="w-full h-full object-contain"
          />
          <div className="absolute left-4 top-4 rounded-full bg-black/60 px-3 py-1 text-sm font-medium text-white">
            {recordingState.status === "requesting" && t("Preparing camera...")}
            {recordingState.status === "recording" && `${t("Recording...")} ${Math.ceil(recordingState.remaining)}s`}
            {recordingState.status === "uploading" && t("Recording complete. Uploading video...")}
          </div>
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-black/60 px-4 py-2 text-xs text-white">
            {t("Recording will stop automatically")}
          </div>
        </div>
      </DialogContent>
    </Dialog>
    <div data-testid="customer-editor" data-template-id={currentTemplate.id} data-current-time={currentTime} className="h-screen md:min-h-screen bg-slate-100 flex flex-col overflow-hidden">
      {/* Toolbar */}
      <header className="bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-semibold">{t(currentTemplate.name)}</h1>
            <p className="text-sm text-slate-500">
              {currentTemplate.canvas_width}×{currentTemplate.canvas_height} • {previewDuration.toFixed(1)}s
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={() => setShowPropertyPanel(!showPropertyPanel)}
            className="hidden md:flex"
          >
            <Settings className="w-4 h-4 mr-2" />
            Properties
          </Button>
          <Button
            onClick={handleExport}
            disabled={isExporting}
            className="btn-primary rounded-full"
            data-testid="export-btn"
          >
            {isExporting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Exporting...
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Export Video
              </>
            )}
          </Button>
        </div>
      </header>

      <div className="flex-1 min-h-0 flex flex-col md:flex-row overflow-hidden">
        {/* Canvas Area */}
        <div className={`shrink-0 md:flex-1 flex flex-col order-1 ${isPreviewFullscreen ? "fixed inset-0 z-50 bg-slate-950" : "h-[33dvh] md:h-auto"}`}>
          <div
            className="flex-1 min-h-0 p-2 md:p-8 overflow-hidden md:overflow-auto"
            onClick={() => setSelectedLayer(null)}
          >
            <div className="relative flex items-center justify-center h-full">
              <Button
                type="button"
                size="icon"
                className="absolute right-2 top-2 z-[60] h-9 w-9 rounded-full btn-primary text-white shadow-md md:hidden"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsPreviewFullscreen((value) => !value);
                }}
                aria-label={isPreviewFullscreen ? "Exit full screen preview" : "Open full screen preview"}
              >
                {isPreviewFullscreen ? <X className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
              </Button>
              {/* Canvas Container */}
              <div
                ref={canvasRef}
                className="relative bg-white shadow-2xl rounded-lg overflow-hidden"
                style={{
                  width: isPreviewFullscreen
                    ? "min(92vw, calc(92vh * var(--canvas-aspect)))"
                    : isMobilePreview
                      ? "min(100%, calc((33dvh - 64px) * var(--canvas-aspect)))"
                      : Math.min(400, currentTemplate.canvas_width / 3),
                  height: isPreviewFullscreen
                    ? "min(92vh, calc(92vw / var(--canvas-aspect)))"
                    : isMobilePreview
                      ? "min(calc(33dvh - 64px), calc(100vw / var(--canvas-aspect)))"
                      : Math.min(600, currentTemplate.canvas_height / 3),
                  "--canvas-aspect": currentTemplate.canvas_width / currentTemplate.canvas_height,
                  aspectRatio: `${currentTemplate.canvas_width} / ${currentTemplate.canvas_height}`,
                }}
                data-testid="video-canvas"
                onClick={(e) => {
                  // If clicking directly on canvas background (not a layer), deselect
                  if (e.target === e.currentTarget) {
                    setSelectedLayer(null);
                  }
                }}
              >
                {/* Layers */}
                {editorTemplate.layers?.map((layer) => {
                  const custom = customizations[layer.id] || {};
                  const mediaUrl = getLayerMediaValue(layer.id, layer, "media_url");
                  const mediaType = getLayerMediaValue(layer.id, layer, "media_type");
                  const mediaFit = getLayerMediaValue(layer.id, layer, "media_fit") || "cover";

                  const animationStyle = getLayerAnimationStyle(
                    layer,
                    customizations,
                    currentTime,
                    isPlaying,
                    currentTemplate.canvas_width,
                    currentTemplate.canvas_height
                  );

                  return (
                    <div
                      key={layer.id}
                      className={`video-canvas-layer ${selectedLayer?.id === layer.id ? "selected" : ""} ${
                        layer.editable ? "editable" : ""
                      }`}
                      style={{
                        ...getLayerStyle(layer),
                        ...animationStyle,
                        ...(layer.music_type ? { opacity: 0, pointerEvents: 'none' } : {}),
                        transition: isPlaying ? 'none' : 'all 0.2s',
                        ...((["image", "video"].includes(layer.type)) ? getMediaCornerRadiusStyle(layer, custom, safeCanvasPreviewScale) : {})
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleLayerSelect(layer);
                      }}
                      data-testid={`layer-${layer.id}`}
                    >
                      {layer.type === "shape" && <ShapeLayer layer={layer} custom={custom} scale={safeCanvasPreviewScale} />}
                      {layer.type === "image" && mediaUrl && (
                        (mediaType === "video") ? (
                          <video
                            ref={el => { if (el) videoRefs.current[layer.id] = el; }}
                            src={mediaUrl}
                            className="w-full h-full"
                            style={{
                              objectFit: mediaFit,
                              objectPosition: `${custom.objectPositionX ?? layer.objectPositionX ?? 50}% ${custom.objectPositionY                       ?? layer.objectPositionY ?? 50}%`
                            }}
                            playsInline
                            preload="auto"
                          />
                        ) : (
                          <img
                            src={mediaUrl}
                            alt={layer.name}
                            className="w-full h-full"
                            style={{
                              objectFit: mediaFit,
                              objectPosition: `${custom.objectPositionX ?? layer.objectPositionX ?? 50}% ${custom.objectPositionY ?? layer.objectPositionY ?? 50}%`
                            }}
                          />
                        )
                      )}
                      {layer.type === "video" && mediaUrl && (
                        <>
                          {/* Render based on stored media_type (video, audio, or image) */}
                          {mediaType === 'audio' ? (
                            layer.music_type ? (
                              <audio
                                ref={el => { if (el) videoRefs.current[layer.id] = el; }}
                                src={mediaUrl}
                                preload="auto"
                              />
                            ) : (
                              <div className="w-full h-full bg-slate-900/90 text-white flex items-center justify-center p-2">
                                <div className="text-center space-y-2">
                                  <Music className="w-8 h-8 mx-auto text-teal-300" />
                                  <p className="text-xs font-medium truncate">{layer.name || t("Audio")}</p>
                                  <audio
                                    ref={el => { if (el) videoRefs.current[layer.id] = el; }}
                                    src={mediaUrl}
                                    controls
                                    preload="auto"
                                    className="w-full max-w-[220px]"
                                  />
                                </div>
                              </div>
                            )
                          ) : mediaType === 'video' || !mediaType ? (
                            <video
                              ref={el => { if (el) videoRefs.current[layer.id] = el; }}
                              src={mediaUrl}
                              className="w-full h-full"
                              style={{
                                objectFit: mediaFit,
                                objectPosition: `${custom.objectPositionX ?? layer.objectPositionX ?? 50}% ${custom.objectPositionY ?? layer.objectPositionY ?? 50}%`
                              }}
                              playsInline
                              preload="auto"
                              loop={false}
                            />
                          ) : (
                            <img
                              src={mediaUrl}
                              alt={layer.name}
                              className="w-full h-full"
                              style={{
                                objectFit: mediaFit,
                                objectPosition: `${custom.objectPositionX ?? layer.objectPositionX ?? 50}% ${custom.objectPositionY ?? layer.objectPositionY ?? 50}%`
                              }}
                            />
                          )}
                        </>
                      )}
                      {layer.type === "video" && !mediaUrl && (
                        <div className="w-full h-full bg-slate-100 flex items-center justify-center pointer-events-none">
                          <Video className="w-8 h-8 text-slate-300" />
                        </div>
                      )}
                      {layer.type === "text" && !(layer.client_review_role === "caption" && !String((custom.content ?? layer.content) ?? "").trim()) && (
                        <TextWipeLayer
                          progress={animationStyle["--text-wipe-progress"]}
                          isExit={animationStyle["--text-wipe-exit"] === 1}
                          className="flex items-center justify-center h-full whitespace-pre-wrap overflow-hidden"
                          style={{
                            fontFamily: custom.fontFamily || layer.fontFamily || "Arial",
                            fontSize: `${calculateAutoFontSize(
                              (layer.width / 100) * (currentTemplate?.canvas_width || 1080),
                              (layer.height / 100) * (currentTemplate?.canvas_height || 1920),
                              custom.content !== undefined ? custom.content : layer.content,
                              {
                                lineHeight: custom.lineHeight || layer.lineHeight || 1.5,
                                fontFamily: custom.fontFamily || layer.fontFamily || "Arial",
                                fontWeight: custom.fontWeight || layer.fontWeight || "normal",
                                fontStyle: custom.fontStyle || layer.fontStyle || "normal"
                              }
                            ) * safeCanvasPreviewScale}px`,
                            fontWeight: custom.fontWeight || layer.fontWeight || "normal",
                            fontStyle: custom.fontStyle || layer.fontStyle || "normal",
                            color: custom.textColor || layer.textColor || "#000000",
                            textShadow: buildTextEffectShadow({
                              strokeWidth: custom.textStrokeWidth ?? layer.textStrokeWidth ?? 0,
                              strokeColor: custom.textStrokeColor ?? layer.textStrokeColor ?? "#000000",
                              scaleDivisor: previewScaleDivisor,
                            }),
                            backgroundColor: (custom.backgroundColor || layer.backgroundColor)
                              ? `${(custom.backgroundColor || layer.backgroundColor)}${Math.round(((custom.backgroundOpacity ?? layer.backgroundOpacity ?? 0) * 255)).toString(16).padStart(2, '0')}`
                              : "transparent",
                            textAlign: custom.textAlign || layer.textAlign || "center",
                            lineHeight: custom.lineHeight || layer.lineHeight || 1.5,
                            justifyContent: (custom.textAlign || layer.textAlign || "center") === "left" ? "flex-start" : (custom.textAlign || layer.textAlign || "center") === "right" ? "flex-end" : "center",
                            borderRadius: `${(custom.borderRadius || layer.borderRadius || 0) * safeCanvasPreviewScale}px`,
                            padding: `${getBackendTextMetrics(
                              (layer.width / 100) * (currentTemplate?.canvas_width || 1080),
                              (layer.height / 100) * (currentTemplate?.canvas_height || 1920),
                              { ...layer, ...custom }
                            ).paddingV * safeCanvasPreviewScale}px ${getBackendTextMetrics(
                              (layer.width / 100) * (currentTemplate?.canvas_width || 1080),
                              (layer.height / 100) * (currentTemplate?.canvas_height || 1920),
                              { ...layer, ...custom }
                            ).paddingH * safeCanvasPreviewScale}px`,
                            border: (custom.borderWidth ?? layer.borderWidth ?? 0) > 0 ? `${(custom.borderWidth ?? layer.borderWidth ?? 0) * safeCanvasPreviewScale}px solid ${custom.borderColor || layer.borderColor || "#000000"}` : "none",
                          }}
                        >
                          {(custom.content ?? layer.content) ?? "Text"}
                        </TextWipeLayer>
                      )}

                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Playback Controls */}
          <div className="bg-white border-t border-slate-200 p-2 md:p-4 shrink-0">
            <div className="flex items-center gap-4">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setIsPlaying(!isPlaying)}
                data-testid="play-btn"
              >
                {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
              </Button>
              <span className="text-sm font-mono text-slate-600 min-w-[100px]">
                {currentTime.toFixed(1)}s / {previewDuration.toFixed(1)}s
              </span>
              <Slider
                data-testid="editor-playhead"
                value={[currentTime]}
                max={previewDuration}
                step={0.1}
                onValueChange={([val]) => setCurrentTime(val)}
                className="flex-1"
              />
            </div>
          </div>
        </div>

        {/* Property Panel */}
        {showPropertyPanel && (
          <div className="flex-1 md:flex-none min-h-0 w-full md:w-[370px] bg-white border-t md:border-t-0 md:border-l border-slate-200 overflow-y-auto order-2 md:order-none p-4">
            <h3 className="font-semibold mb-4">Properties</h3>

            {currentTemplate?.source === "client_review" && (
              <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 p-3">
                <p className="text-xs font-medium text-slate-600 mb-2">{t("Client review tools")}</p>
                <div className="grid grid-cols-1 gap-2">
                  {editorTemplate.layers?.filter((layer) => ["video", "logo", "caption"].includes(layer.client_review_role)).map((layer) => (
                    <Button
                      key={layer.id}
                      type="button"
                      variant={selectedLayer?.id === layer.id ? "default" : "outline"}
                      size="sm"
                      className="justify-start"
                      onClick={() => setSelectedLayer(layer)}
                    >
                      {layer.client_review_role === "video" && t("Trim recorded video")}
                      {layer.client_review_role === "logo" && t("Optional logo")}
                      {layer.client_review_role === "caption" && t("Optional caption")}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {customTimelineResult.timeline && (
              <div className="mb-6 rounded-xl border border-teal-200 bg-teal-50/50 p-3" data-testid="custom-scene-builder">
                <div className="mb-3 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-teal-900">{t("Custom video scenes")}</p>
                    <p className="text-xs text-teal-700">{t("Video clips set each scene's duration automatically.")}</p>
                  </div>
                  <span className="text-xs font-mono text-teal-800">{customTimelineResult.duration.toFixed(1)}s</span>
                </div>
                <div className="space-y-3">
                  {customTimelineResult.timeline.groups.map((group, index) => {
                    const mediaLayer = customTimelineResult.layers.find((layer) => layer.id === `${group.id}:media`);
                    const sceneLayers = customTimelineResult.layers.filter((layer) => layer.group_id === group.id);
                    const sceneChildren = sceneLayers.filter((layer) => layer.id !== mediaLayer?.id);
                    const selectedSceneLayer = sceneLayers.find((layer) => layer.id === selectedLayer?.id);
                    const mediaCustom = customizations[mediaLayer?.id] || {};
                    const automatic = mediaCustom.media_type === "video" && Number(mediaCustom.source_duration) > 0;
                    return (
                      <div key={group.id} className="rounded-lg border border-slate-200 bg-white p-2">
                        <div className="flex items-center gap-2">
                          <div className="min-w-0 flex-1 text-left">
                            <span className="block text-xs font-semibold">{t("Scene")} {index + 1}</span>
                            <span className="block text-xs text-slate-500">{t("Duration")}</span>
                          </div>
                          <Input
                            type="number" min="0.1" step="0.5"
                            className="h-8 w-20"
                            value={Number(mediaLayer?.duration || group.duration).toFixed(1)}
                            disabled={automatic}
                            title={automatic ? t("Duration follows the uploaded video") : t("Scene duration")}
                            onChange={(event) => setCustomSceneDuration(group.id, event.target.value)}
                          />
                        </div>
                        <div className="mt-2 space-y-1">
                          {[mediaLayer, ...sceneChildren].filter(Boolean).map((layer) => (
                            <button
                              key={layer.id}
                              type="button"
                              className={`flex w-full items-center gap-2 rounded-md p-2 text-left text-xs transition-colors ${
                                selectedLayer?.id === layer.id
                                  ? "bg-teal-100 text-teal-800"
                                  : "bg-slate-50 text-slate-700 hover:bg-slate-100"
                              }`}
                              onClick={() => handleLayerSelect(layer)}
                            >
                              {layer.type === "video" && <Video className="h-4 w-4 shrink-0" />}
                              {layer.type === "text" && <Type className="h-4 w-4 shrink-0" />}
                              {layer.type === "shape" && <Square className="h-4 w-4 shrink-0" />}
                              <span className="min-w-0 flex-1 truncate">{t(layer.name)}</span>
                              {layer.id === mediaLayer?.id && (
                                <span className="truncate text-[10px] text-slate-500">
                                  {mediaCustom.media_url ? t("Media uploaded") : t("Select to upload media")}
                                </span>
                              )}
                            </button>
                          ))}
                        </div>
                        {selectedSceneLayer && (
                          <div className="mt-2 rounded-lg border border-teal-100 bg-white p-3 shadow-sm">
                            {selectedLayerProperties}
                          </div>
                        )}
                        <div className="mt-2 flex gap-2">
                          <Button type="button" size="sm" variant="outline" className="h-7 flex-1" onClick={() => addCustomSceneLayer(group.id, "text")}>
                            <Type className="mr-1 h-3 w-3" /> {t("Add Text")}
                          </Button>
                          <Button type="button" size="sm" variant="outline" className="h-7 flex-1" onClick={() => addCustomSceneLayer(group.id, "shape")}>
                            <Square className="mr-1 h-3 w-3" /> {t("Add Shape")}
                          </Button>
                        </div>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="mt-2 h-7 w-full border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                          onClick={() => deleteCustomScene(group.id)}
                          disabled={recordingState.isOpen && recordingState.layerId === mediaLayer?.id}
                        >
                          <Trash2 className="mr-1 h-3 w-3" /> {t(`Delete Scene ${index + 1}`)}
                        </Button>
                      </div>
                    );
                  })}
                </div>
                <Button type="button" variant="outline" className="mt-3 w-full border-dashed" onClick={addCustomScene}>
                  <Plus className="mr-2 h-4 w-4" /> {t("Add scene")}
                </Button>
              </div>
            )}

            {/* Editable Layers List */}
            <div className="mt-6 pt-6 border-t border-slate-200">
              <div className="property-group-title mb-2">{t("Editable Layers")} ({editableLayers.length})</div>
              <div className="space-y-2">
                {editableLayers.map((layer) => (
                  <div
                    key={layer.id}
                    ref={(element) => {
                      if (element) {
                        layerListItemRefs.current[layer.id] = element;
                      } else {
                        delete layerListItemRefs.current[layer.id];
                      }
                    }}
                  >
                  <button
                    className={`w-full text-left p-2 rounded-lg text-sm transition-colors ${
                      selectedLayer?.id === layer.id
                        ? "bg-teal-100 text-teal-700"
                        : "hover:bg-slate-100"
                    }`}
                    data-testid={`editable-layer-${layer.id}`}
                    data-layer-name={layer.name}
                    aria-pressed={selectedLayer?.id === layer.id}
                    onClick={() => handleLayerSelect(layer)}
                  >
                    <div className="flex items-center gap-2">
                      {layer.type === "image" && <Image className="w-4 h-4" />}
                      {layer.type === "video" && <Video className="w-4 h-4" />}
                      {layer.type === "text" && <Type className="w-4 h-4" />}
                      {layer.type === "shape" && <Square className="w-4 h-4" />}
                      <span className="truncate">{t(layer.name)}</span>
                      {layer.pixverse_prompt && <Sparkles className="w-3 h-3 text-teal-500" />}
                    </div>
                  </button>
                  {selectedLayer?.id === layer.id && (
                    <div className="mt-3 rounded-xl border border-teal-100 bg-white p-3 shadow-sm">
                      {selectedLayerProperties}
                    </div>
                  )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
    </>
  );
}
